import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import PromoShowcaseBuilderWorkspace from './PromoShowcaseBuilderWorkspace';
import TemplateGallery from './TemplateGallery';
import PageTemplateGallery from './PageTemplateGallery';
import PromoShowcaseStockMediaDialog from './PromoShowcaseStockMediaDialog';
import { PAGE_TEMPLATE_TYPES } from './foundation/pageTemplateRegistry';
import { BUILDER_SIDEBAR_VIEWS } from './BuilderSidebar';
import { BUILDER_DEVICE_MODES } from './builderArchitecture';

const SITE_FONT_OPTIONS = [
  { value: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', label: 'Inter' },
  { value: '"Manrope", Inter, system-ui, sans-serif', label: 'Manrope' },
  { value: '"DM Sans", Inter, system-ui, sans-serif', label: 'DM Sans' },
  { value: '"Plus Jakarta Sans", Inter, system-ui, sans-serif', label: 'Plus Jakarta Sans' },
  { value: '"Montserrat", Inter, system-ui, sans-serif', label: 'Montserrat' },
  { value: '"Poppins", Inter, system-ui, sans-serif', label: 'Poppins' },
  { value: '"Roboto", Arial, sans-serif', label: 'Roboto' },
  { value: '"Open Sans", Arial, sans-serif', label: 'Open Sans' },
  { value: '"Lato", Arial, sans-serif', label: 'Lato' },
  { value: '"Nunito Sans", Inter, system-ui, sans-serif', label: 'Nunito Sans' },
  { value: '"Merriweather", Georgia, serif', label: 'Merriweather' },
  { value: '"Oswald", Arial, sans-serif', label: 'Oswald' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: '"Helvetica Neue", Helvetica, Arial, sans-serif', label: 'Helvetica Neue' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Trebuchet MS", Arial, sans-serif', label: 'Trebuchet MS' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
];

/**
 * The live PromoShowcase builder presentation layer.
 *
 * Important: this component deliberately owns no persistence and makes no API
 * requests. Every mutation/publish action is delegated back to the existing
 * PromoShowcasePage handlers so WordPress synchronization and request payloads
 * remain unchanged.
 */
export default function PromoShowcaseBuilderIntegrated({
  templates = [],
  selectedTemplate,
  selectedTemplateId,
  selectTemplateAndHydrate,
  loadTemplates,
  resetBuilderFromSelectedTemplate,
  templateForm,
  setTemplateForm,
  templateStatus,
  setTemplateStatus,
  settings,
  statusChipSx,
  layoutBuilder,
  builderSlots = [],
  slotMeta = {},
  selectedBuilderSlot,
  setSelectedBuilderSlot,
  selectedBuilderSlotMeta,
  currentBuilderWidgets = [],
  selectedBuilderWidget,
  selectedBuilderWidgetIndex,
  setSelectedBuilderWidgetIndex,
  selectedBuilderWidgetMeta,
  selectedBuilderWidgetPresets = [],
  getWidgetLabel,
  getWidgetMeta,
  widgetLibrary = [],
  widgetDetails = {},
  quickSections = [],
  insertQuickSection,
  widgetDraft,
  setWidgetDraft,
  addWidgetToSlot,
  updateWidgetProps,
  addHeroSlide,
  updateHeroSlide,
  removeHeroSlide,
  applyWidgetTemplatePreset,
  moveWidgetInSlot,
  reorderWidgetInSlot,
  removeWidgetFromSlot,
  builderCanvasMode,
  setBuilderCanvasMode,
  previewSelectedSelector,
  previewHoveredSelector,
  showSelectorOverlay,
  setShowSelectorOverlay,
  insertSelectorIntoCustomCss,
  templateStylesPreviewSrcDoc,
  previewRendererMode = 'local',
  setPreviewRendererMode,
  wordpressPreviewLoading = false,
  wordpressPreviewError = '',
  renderWidgetPreviewCard,
  templateCssEditorTab,
  setTemplateCssEditorTab,
  showSelectorContract,
  setShowSelectorContract,
  templatesApplying,
  templatesSaving,
  applySelectedTemplate,
  handleResyncNow,
  handleRollbackLatest,
  savePrivateTemplate,
  updateSelectedPrivateTemplate,
  lastApplyResult,
  templateFormErrors = [],
  publishValidationRef,
  styles,
  brand,
  siteUrl,
  onImportPageTemplate,
  layoutDirty = false,
  canUndoLayoutImport = false,
  onUndoLayoutImport,
  showcasePages = [],
  addShowcasePage,
  updateShowcasePage,
  removeShowcasePage,
  requestedSidebarView = '',
  requestedSidebarViewTick = 0,
  instanceUniqueId,
  onUploadShowcaseMedia,
  focusMode = false,
  onCloseFocusMode,
}) {
  const [sidebarView, setSidebarView] = useState(BUILDER_SIDEBAR_VIEWS.ELEMENTS);
  const [deviceMode, setDeviceMode] = useState(BUILDER_DEVICE_MODES.DESKTOP);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [showPageLayouts, setShowPageLayouts] = useState(false);
  const [stockMediaSlideIndex, setStockMediaSlideIndex] = useState(null);
  const [quickSectionCategory, setQuickSectionCategory] = useState('All');
  const [siteStylesSection, setSiteStylesSection] = useState('global');
  const [logoUploading, setLogoUploading] = useState(false);
  const [slideBackgroundUploadingIndex, setSlideBackgroundUploadingIndex] = useState(null);
  const [pendingSlideBackgroundIndex, setPendingSlideBackgroundIndex] = useState(null);
  const logoFileInputRef = React.useRef(null);
  const slideBackgroundFileInputRef = React.useRef(null);
  const previewFrameRef = React.useRef(null);
  const previewDragCleanupRef = React.useRef(null);
  const previewInlineCleanupRef = React.useRef(null);
  const previewScrollPositionRef = React.useRef({ top: 0, left: 0 });
  const [previewInlineEditing, setPreviewInlineEditing] = useState(false);
  const [renderedPreviewSrcDoc, setRenderedPreviewSrcDoc] = useState(templateStylesPreviewSrcDoc);
  const latestBuilderWidgetsRef = React.useRef(currentBuilderWidgets);
  const previewHardLoadKeyRef = React.useRef(`${selectedBuilderSlot}::${previewRendererMode}`);
  const previewSoftPatchQueuedRef = React.useRef(false);
  const previewSourceDocRef = React.useRef(templateStylesPreviewSrcDoc);
  const previewInlineCommitTimersRef = React.useRef(new Map());
  const previewInlinePendingCommitsRef = React.useRef(new Map());

  latestBuilderWidgetsRef.current = currentBuilderWidgets;

  React.useEffect(() => {
    const nextKey = `${selectedBuilderSlot}::${previewRendererMode}`;
    if (previewHardLoadKeyRef.current === nextKey) return;
    previewHardLoadKeyRef.current = nextKey;
    previewSourceDocRef.current = templateStylesPreviewSrcDoc;
    setRenderedPreviewSrcDoc(templateStylesPreviewSrcDoc);
  }, [previewRendererMode, selectedBuilderSlot, templateStylesPreviewSrcDoc]);

  React.useEffect(() => {
    if (requestedSidebarView && Object.values(BUILDER_SIDEBAR_VIEWS).includes(requestedSidebarView)) {
      setSidebarView(requestedSidebarView);
    }
  }, [requestedSidebarView, requestedSidebarViewTick]);

  React.useEffect(() => {
    if (selectedBuilderWidget) setSidebarView(BUILDER_SIDEBAR_VIEWS.INSPECTOR);
  }, [selectedBuilderWidget]);

  const selectedSelector = previewSelectedSelector || previewHoveredSelector;
  const syncStatus = settings?.templateSyncState?.status || 'unknown';
  const currentPage = (showcasePages || []).find((page) => page?.slot === selectedBuilderSlot) || null;
  const pageIdentity = `${currentPage?.title || ''} ${currentPage?.slug || ''} ${selectedBuilderSlot || ''}`.toLowerCase();
  const pageTemplateType = selectedBuilderSlot === 'home'
    ? PAGE_TEMPLATE_TYPES.HOME
    : selectedBuilderSlot === 'about'
      ? PAGE_TEMPLATE_TYPES.ABOUT
      : selectedBuilderSlot === 'contact'
        ? PAGE_TEMPLATE_TYPES.CONTACT
        : pageIdentity.includes('print') || pageIdentity.includes('sign') || pageIdentity.includes('apparel')
          ? PAGE_TEMPLATE_TYPES.PRINT_SERVICES
          : pageIdentity.includes('portfolio') || pageIdentity.includes('work') || pageIdentity.includes('gallery')
            ? PAGE_TEMPLATE_TYPES.PORTFOLIO
            : pageIdentity.includes('store') || pageIdentity.includes('event') || pageIdentity.includes('program')
              ? PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS
              : PAGE_TEMPLATE_TYPES.SERVICES;

  const pageTemplateAllowedTypes = selectedBuilderSlot === 'home'
    ? [PAGE_TEMPLATE_TYPES.HOME]
    : selectedBuilderSlot === 'about'
      ? [PAGE_TEMPLATE_TYPES.ABOUT, PAGE_TEMPLATE_TYPES.PORTFOLIO]
      : selectedBuilderSlot === 'contact'
        ? [PAGE_TEMPLATE_TYPES.CONTACT]
        : [
            PAGE_TEMPLATE_TYPES.SERVICES,
            PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS,
            PAGE_TEMPLATE_TYPES.PRINT_SERVICES,
            PAGE_TEMPLATE_TYPES.PORTFOLIO,
          ];

  const canUsePageLayouts = Boolean(onImportPageTemplate);
  const stockMediaQuery = useMemo(() => {
    const explicit = String(selectedBuilderWidget?.props?.stock_search_query || '').trim();
    if (explicit) return explicit;
    if (selectedBuilderSlot === 'services') return 'creative branding team promotional products decoration';
    if (selectedBuilderSlot === 'about') return 'business team collaboration office authentic';
    if (selectedBuilderSlot === 'contact') return 'customer service team office friendly';
    if (selectedBuilderSlot === 'catalog') return 'branded merchandise products lifestyle flat lay';
    return 'business branded merchandise lifestyle office people';
  }, [selectedBuilderSlot, selectedBuilderWidget?.props?.stock_search_query]);

  const softPatchPreviewDocument = React.useCallback((nextHtml) => {
    const frame = previewFrameRef.current;
    const doc = frame?.contentDocument;
    const previewWindow = frame?.contentWindow;
    if (!doc || !previewWindow || !nextHtml) return { patched: false, contentChanged: false };

    try {
      const parser = new previewWindow.DOMParser();
      const previousHtml = previewSourceDocRef.current || '';
      const previousDoc = previousHtml ? parser.parseFromString(previousHtml, 'text/html') : null;
      const nextDoc = parser.parseFromString(nextHtml, 'text/html');
      const currentRoot = doc.querySelector('.promoshowcase-slot');
      const nextRoot = nextDoc.querySelector('.promoshowcase-slot');
      const previousRoot = previousDoc?.querySelector('.promoshowcase-slot') || null;
      const contentChanged = !previousRoot || previousRoot.innerHTML !== nextRoot?.innerHTML;
      const currentStyle = Array.from(doc.head?.querySelectorAll('style') || [])
        .find((node) => !node.hasAttribute('data-ps-builder-drag-style') && !node.hasAttribute('data-ps-builder-inline-style'));
      const nextStyle = Array.from(nextDoc.head?.querySelectorAll('style') || [])[0];

      if (!currentRoot || !nextRoot) return { patched: false, contentChanged: false };

      const savedScroll = {
        top: Number(previewWindow.scrollY || doc.documentElement?.scrollTop || doc.body?.scrollTop || previewScrollPositionRef.current?.top || 0),
        left: Number(previewWindow.scrollX || doc.documentElement?.scrollLeft || doc.body?.scrollLeft || previewScrollPositionRef.current?.left || 0),
      };
      previewScrollPositionRef.current = savedScroll;

      if (currentStyle && nextStyle) currentStyle.textContent = nextStyle.textContent;

      const currentGoogleFonts = doc.head?.querySelector('link[data-ps-google-fonts="true"]');
      const nextGoogleFonts = nextDoc.head?.querySelector('link[data-ps-google-fonts="true"]');
      const nextHeaderFont = String(
        nextDoc.querySelector('.ps-preview-header')?.style?.getPropertyValue('--ps-header-font')
        || nextDoc.querySelector('.ps-site-header')?.style?.fontFamily
        || ''
      ).trim();
      const nextHeaderPrimaryFamily = nextHeaderFont
        ? String(nextHeaderFont).split(',')[0].trim().replace(/^['"]|['"]$/g, '')
        : '';

      let googleFontStylesheetReady = Promise.resolve();
      if (nextGoogleFonts) {
        const nextHref = String(nextGoogleFonts.getAttribute('href') || '');
        if (currentGoogleFonts) {
          const currentHref = String(currentGoogleFonts.getAttribute('href') || '');
          if (currentHref !== nextHref) {
            googleFontStylesheetReady = new Promise((resolve) => {
              const finish = () => resolve();
              currentGoogleFonts.addEventListener('load', finish, { once: true });
              currentGoogleFonts.addEventListener('error', finish, { once: true });
              currentGoogleFonts.setAttribute('href', nextHref);
            });
          } else if (!currentGoogleFonts.sheet) {
            googleFontStylesheetReady = new Promise((resolve) => {
              const finish = () => resolve();
              currentGoogleFonts.addEventListener('load', finish, { once: true });
              currentGoogleFonts.addEventListener('error', finish, { once: true });
              previewWindow.setTimeout(finish, 2500);
            });
          }
        } else {
          const clone = nextGoogleFonts.cloneNode(true);
          googleFontStylesheetReady = new Promise((resolve) => {
            const finish = () => resolve();
            clone.addEventListener('load', finish, { once: true });
            clone.addEventListener('error', finish, { once: true });
            previewWindow.setTimeout(finish, 2500);
          });
          doc.head?.appendChild(clone);
        }
      } else if (currentGoogleFonts) {
        currentGoogleFonts.remove();
      }

      const patchElement = (currentElement, nextElement) => {
        if (!currentElement || !nextElement) return false;
        currentElement.className = nextElement.className;
        Array.from(currentElement.attributes).forEach((attr) => {
          if (!nextElement.hasAttribute(attr.name)) currentElement.removeAttribute(attr.name);
        });
        Array.from(nextElement.attributes).forEach((attr) => currentElement.setAttribute(attr.name, attr.value));
        currentElement.innerHTML = nextElement.innerHTML;
        return true;
      };

      // Keep the live block DOM completely stable for style-only changes
      // (fonts, colours, spacing, borders, header settings, etc.). Replacing
      // this root causes :hover, drag handles and inline-edit chrome to repaint.
      if (contentChanged) patchElement(currentRoot, nextRoot);

      // Site chrome lives outside .promoshowcase-slot. Patch it alongside the
      // page body so Header/Footer Site Styles update live without replacing
      // the iframe document (which would reset scroll/editor state).
      [
        '.ps-preview-announcement',
        '.ps-preview-header',
        '.ps-preview-footer',
        '.ps-site-header',
        '.ps-site-footer',
      ].forEach((selector) => {
        const currentElement = doc.querySelector(selector);
        const nextElement = nextDoc.querySelector(selector);

        if (currentElement && nextElement) {
          patchElement(currentElement, nextElement);
          return;
        }

        if (currentElement && !nextElement) {
          currentElement.remove();
          return;
        }

        if (!currentElement && nextElement) {
          const clone = nextElement.cloneNode(true);
          if (selector.includes('footer')) {
            currentRoot.parentNode?.insertBefore(clone, currentRoot.nextSibling);
          } else {
            currentRoot.parentNode?.insertBefore(clone, currentRoot);
          }
        }
      });

      const restore = () => previewWindow.scrollTo({
        top: Number(savedScroll.top || 0),
        left: Number(savedScroll.left || 0),
        behavior: 'auto',
      });
      restore();
      previewWindow.requestAnimationFrame?.(restore);

      if (doc.fonts?.ready) {
        googleFontStylesheetReady
          .then(async () => {
            // A changed stylesheet href is not part of FontFaceSet until the
            // stylesheet itself has loaded. Explicitly request the header font
            // after that point so Google fonts are deterministic in srcDoc.
            if (nextHeaderPrimaryFamily && typeof doc.fonts.load === 'function') {
              try {
                await Promise.all([
                  doc.fonts.load(`400 16px "${nextHeaderPrimaryFamily}"`),
                  doc.fonts.load(`700 16px "${nextHeaderPrimaryFamily}"`),
                ]);
              } catch (_error) {
                // Keep the CSS fallback stack if the remote font is unavailable.
              }
            }
            await doc.fonts.ready;
          })
          .then(() => {
            previewWindow.requestAnimationFrame?.(() => {
              // Force a clean layout pass after a newly selected web font is ready.
              void doc.body?.offsetHeight;
              restore();
            });
          })
          .catch(() => {});
      }

      previewSourceDocRef.current = nextHtml;
      return { patched: true, contentChanged };
    } catch (error) {
      console.warn('[PromoShowcase preview] soft patch failed; keeping current iframe document', error);
      return { patched: false, contentChanged: false };
    }
  }, []);

  const setPreviewHeroHeight = React.useCallback((nodeId, rawHeight) => {
    const frame = previewFrameRef.current;
    const doc = frame?.contentDocument;
    if (!doc || !nodeId) return;

    const height = Math.max(220, Math.min(1200, Number(rawHeight || 0)));
    if (!Number.isFinite(height)) return;

    const block = Array.from(doc.querySelectorAll('[data-ps-node]'))
      .find((node) => String(node.getAttribute('data-ps-node') || '') === String(nodeId));
    if (!block) return;

    block.style.minHeight = `${height}px`;

    // Instant preview uses the section itself + .ps-hero__grid.
    const instantGrid = block.querySelector('.ps-hero__grid');
    if (instantGrid) instantGrid.style.minHeight = `${height}px`;

    // WordPress runtime uses a slider wrapper with slide min-heights.
    const heroRoot = block.querySelector('.ps-hero');
    if (heroRoot) heroRoot.style.minHeight = `${height}px`;
    block.querySelectorAll('.ps-hero-slider__slide').forEach((slide) => {
      slide.style.minHeight = `${height}px`;
    });
  }, []);

  const installPreviewDragAndDrop = React.useCallback(() => {
    if (previewDragCleanupRef.current) {
      previewDragCleanupRef.current();
      previewDragCleanupRef.current = null;
    }

    const frame = previewFrameRef.current;
    const doc = frame?.contentDocument;
    const previewWindow = frame?.contentWindow;
    if (!doc || !previewWindow || typeof reorderWidgetInSlot !== 'function') return;

    const savedScroll = { ...(previewScrollPositionRef.current || { top: 0, left: 0 }) };
    const restoreScrollPosition = () => {
      previewWindow.scrollTo({
        top: Number(savedScroll.top || 0),
        left: Number(savedScroll.left || 0),
        behavior: 'auto',
      });
    };
    // A srcDoc replacement creates a fresh document at scrollY=0. Restore more
    // than once because images/fonts can change the document height immediately
    // after load and browsers may otherwise clamp/reset the first scroll call.
    restoreScrollPosition();
    previewWindow.requestAnimationFrame?.(() => {
      restoreScrollPosition();
      previewWindow.requestAnimationFrame?.(restoreScrollPosition);
    });
    previewWindow.setTimeout?.(restoreScrollPosition, 60);
    previewWindow.setTimeout?.(restoreScrollPosition, 180);

    const nodeIndex = new Map(
      currentBuilderWidgets
        .map((widget, index) => [String(widget?.id || ''), index])
        .filter(([nodeId]) => nodeId)
    );
    if (!nodeIndex.size) return;

    const blocks = Array.from(doc.querySelectorAll('[data-ps-node]'))
      .filter((block) => nodeIndex.has(String(block.getAttribute('data-ps-node') || '')));
    if (!blocks.length) return;

    const style = doc.createElement('style');
    style.setAttribute('data-ps-builder-drag-style', 'true');
    style.textContent = [
      '[data-ps-node].ps-builder-draggable{transition:opacity .14s ease,box-shadow .14s ease,transform .14s ease;}',
      '[data-ps-node].ps-builder-dragging{opacity:.38!important;}',
      '.ps-builder-preview-drag-handle{position:absolute!important;top:8px!important;left:8px!important;z-index:50!important;width:30px!important;height:30px!important;border:0!important;border-radius:999px!important;background:#fff!important;color:#475569!important;box-shadow:0 4px 14px rgba(15,23,42,.18)!important;display:grid!important;place-items:center!important;font:700 15px/1 system-ui,sans-serif!important;cursor:grab!important;opacity:0!important;pointer-events:none!important;transform:translateY(-2px)!important;transition:opacity .12s ease,transform .12s ease!important;user-select:none!important;-webkit-user-select:none!important;}',
      '.ps-builder-preview-drag-handle:active{cursor:grabbing!important;}',
      '.ps-builder-preview-drag-handle:hover{background:#f8fafc!important;}',
      '[data-ps-node].ps-builder-drop-before{box-shadow:0 -4px 0 #C3171D!important;}',
      '[data-ps-node].ps-builder-drop-after{box-shadow:0 4px 0 #C3171D!important;}',
      '.ps-builder-preview-delete{position:absolute!important;top:8px!important;right:8px!important;z-index:50!important;width:30px!important;height:30px!important;border:0!important;border-radius:999px!important;background:#fff!important;color:#C3171D!important;box-shadow:0 4px 14px rgba(15,23,42,.18)!important;display:grid!important;place-items:center!important;font:700 18px/1 system-ui,sans-serif!important;cursor:pointer!important;opacity:0!important;pointer-events:none!important;transform:translateY(-2px)!important;transition:opacity .12s ease,transform .12s ease,background .12s ease!important;}',
      '.ps-builder-preview-delete:hover{background:#fff5f5!important;}',
      '[data-ps-node]:hover>.ps-builder-preview-drag-handle,[data-ps-node]:focus-within>.ps-builder-preview-drag-handle,[data-ps-node].ps-builder-dragging>.ps-builder-preview-drag-handle,[data-ps-node]:hover>.ps-builder-preview-delete,[data-ps-node]:focus-within>.ps-builder-preview-delete{opacity:.94!important;pointer-events:auto!important;transform:translateY(0)!important;}',
      '.ps-builder-hero-resize{position:absolute!important;left:0!important;right:0!important;bottom:-7px!important;height:14px!important;z-index:55!important;cursor:ns-resize!important;opacity:0!important;pointer-events:none!important;display:flex!important;align-items:center!important;justify-content:center!important;transition:opacity .12s ease!important;touch-action:none!important;}',
      '.ps-builder-hero-resize::before{content:""!important;width:54px!important;height:4px!important;border-radius:999px!important;background:#C3171D!important;box-shadow:0 1px 5px rgba(15,23,42,.24)!important;}',
      '.ps-builder-hero-resize-label{position:absolute!important;bottom:15px!important;left:50%!important;transform:translateX(-50%)!important;padding:3px 7px!important;border-radius:999px!important;background:rgba(15,23,42,.92)!important;color:#fff!important;font:700 10px/1.2 system-ui,sans-serif!important;white-space:nowrap!important;opacity:0!important;pointer-events:none!important;}',
      '[data-ps-node]:hover>.ps-builder-hero-resize,[data-ps-node]:focus-within>.ps-builder-hero-resize,[data-ps-node].ps-builder-hero-resizing>.ps-builder-hero-resize{opacity:1!important;pointer-events:auto!important;}',
      '[data-ps-node].ps-builder-hero-resizing>.ps-builder-hero-resize-label{opacity:1!important;}',
    ].join('');
    doc.head?.appendChild(style);

    let draggedNodeId = '';
    let dropTarget = null;
    const listeners = [];

    const rememberScrollPosition = () => {
      previewScrollPositionRef.current = {
        top: Number(previewWindow.scrollY || doc.documentElement?.scrollTop || doc.body?.scrollTop || 0),
        left: Number(previewWindow.scrollX || doc.documentElement?.scrollLeft || doc.body?.scrollLeft || 0),
      };
    };
    previewWindow.addEventListener('scroll', rememberScrollPosition, { passive: true });

    const clearDropTarget = () => {
      if (!dropTarget) return;
      dropTarget.classList.remove('ps-builder-drop-before', 'ps-builder-drop-after');
      dropTarget = null;
    };

    const listen = (element, type, handler) => {
      element.addEventListener(type, handler);
      listeners.push([element, type, handler]);
    };

    blocks.forEach((block) => {
      block.classList.add('ps-builder-draggable');

      const nodeId = String(block.getAttribute('data-ps-node') || '');
      const dragHandle = doc.createElement('button');
      dragHandle.type = 'button';
      dragHandle.className = 'ps-builder-preview-drag-handle';
      dragHandle.setAttribute('aria-label', 'Drag section');
      dragHandle.setAttribute('title', 'Drag section');
      dragHandle.setAttribute('draggable', 'true');
      dragHandle.textContent = '⋮⋮';
      block.appendChild(dragHandle);

      const deleteButton = doc.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'ps-builder-preview-delete';
      deleteButton.setAttribute('aria-label', 'Delete section');
      deleteButton.setAttribute('title', 'Delete section');
      deleteButton.textContent = '×';
      block.appendChild(deleteButton);

      const widgetIndex = nodeIndex.get(nodeId);
      const widget = Number.isInteger(widgetIndex) ? currentBuilderWidgets[widgetIndex] : null;
      const isHero = String(widget?.component || '') === 'hero';
      let resizeHandle = null;
      let resizeLabel = null;

      if (isHero) {
        resizeHandle = doc.createElement('div');
        resizeHandle.className = 'ps-builder-hero-resize';
        resizeHandle.setAttribute('role', 'separator');
        resizeHandle.setAttribute('aria-orientation', 'horizontal');
        resizeHandle.setAttribute('aria-label', 'Resize banner height');
        resizeHandle.setAttribute('title', 'Drag to resize banner');

        resizeLabel = doc.createElement('span');
        resizeLabel.className = 'ps-builder-hero-resize-label';
        resizeLabel.textContent = `${Math.max(220, Math.min(1200, Number(widget?.props?.min_height || block.getBoundingClientRect().height || 460)))}px`;
        block.appendChild(resizeHandle);
        block.appendChild(resizeLabel);

        let resizeStartY = 0;
        let resizeStartHeight = 0;
        let resizeHeight = 0;
        let resizeRaf = null;

        const applyResizeFrame = () => {
          resizeRaf = null;
          setPreviewHeroHeight(nodeId, resizeHeight);
          if (resizeLabel) resizeLabel.textContent = `${resizeHeight}px`;
        };

        const onResizeMove = (event) => {
          event.preventDefault();
          const delta = Number(event.clientY || 0) - resizeStartY;
          resizeHeight = Math.max(220, Math.min(1200, Math.round(resizeStartHeight + delta)));
          if (!resizeRaf) resizeRaf = previewWindow.requestAnimationFrame(applyResizeFrame);
        };

        const finishResize = (event) => {
          if (event) event.preventDefault();
          previewWindow.removeEventListener('pointermove', onResizeMove);
          previewWindow.removeEventListener('pointerup', finishResize);
          previewWindow.removeEventListener('pointercancel', finishResize);
          if (resizeRaf) {
            previewWindow.cancelAnimationFrame?.(resizeRaf);
            resizeRaf = null;
          }
          setPreviewHeroHeight(nodeId, resizeHeight);
          block.classList.remove('ps-builder-hero-resizing');
          if (Number.isInteger(widgetIndex) && Number.isFinite(resizeHeight)) {
            updateWidgetProps(selectedBuilderSlot, widgetIndex, 'min_height', resizeHeight);
          }
        };

        listen(resizeHandle, 'pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          resizeStartY = Number(event.clientY || 0);
          resizeStartHeight = Math.max(
            220,
            Math.min(
              1200,
              Number(widget?.props?.min_height || block.getBoundingClientRect().height || 460)
            )
          );
          resizeHeight = resizeStartHeight;
          block.classList.add('ps-builder-hero-resizing');
          resizeLabel.textContent = `${resizeHeight}px`;
          try { resizeHandle.setPointerCapture?.(event.pointerId); } catch (_error) { /* browser fallback */ }
          previewWindow.addEventListener('pointermove', onResizeMove, { passive: false });
          previewWindow.addEventListener('pointerup', finishResize, { passive: false });
          previewWindow.addEventListener('pointercancel', finishResize, { passive: false });
        });
      }

      listen(deleteButton, 'mousedown', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
      listen(deleteButton, 'dragstart', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
      listen(deleteButton, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        rememberScrollPosition();
        const index = nodeIndex.get(nodeId);
        if (Number.isInteger(index)) {
          removeWidgetFromSlot(selectedBuilderSlot, index);
          setSelectedBuilderWidgetIndex((current) => {
            if (current == null) return current;
            if (current === index) return null;
            return current > index ? current - 1 : current;
          });
        }
      });

      listen(dragHandle, 'mousedown', (event) => {
        event.stopPropagation();
      });
      listen(dragHandle, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
      listen(dragHandle, 'dragstart', (event) => {
        event.stopPropagation();
        draggedNodeId = nodeId;
        block.classList.add('ps-builder-dragging');
        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed = 'move';
          try { event.dataTransfer.setData('text/plain', draggedNodeId); } catch (error) { /* browser fallback */ }
        }
      });

      listen(block, 'dragover', (event) => {
        const targetNodeId = String(block.getAttribute('data-ps-node') || '');
        if (!draggedNodeId || draggedNodeId === targetNodeId) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        clearDropTarget();
        const rect = block.getBoundingClientRect();
        const position = event.clientY < rect.top + (rect.height / 2) ? 'before' : 'after';
        block.classList.add(position === 'before' ? 'ps-builder-drop-before' : 'ps-builder-drop-after');
        dropTarget = block;
      });

      listen(block, 'dragleave', (event) => {
        if (event.relatedTarget && block.contains(event.relatedTarget)) return;
        if (dropTarget === block) clearDropTarget();
      });

      listen(block, 'drop', (event) => {
        const targetNodeId = String(block.getAttribute('data-ps-node') || '');
        if (!draggedNodeId || draggedNodeId === targetNodeId) return;
        event.preventDefault();
        event.stopPropagation();

        const rect = block.getBoundingClientRect();
        const position = event.clientY < rect.top + (rect.height / 2) ? 'before' : 'after';
        const fromIndex = nodeIndex.get(draggedNodeId);
        const targetIndex = nodeIndex.get(targetNodeId);
        if (Number.isInteger(fromIndex) && Number.isInteger(targetIndex)) {
          let toIndex = targetIndex + (position === 'after' ? 1 : 0);
          if (fromIndex < toIndex) toIndex -= 1;
          toIndex = Math.max(0, Math.min(currentBuilderWidgets.length - 1, toIndex));
          if (fromIndex !== toIndex) reorderWidgetInSlot(selectedBuilderSlot, fromIndex, toIndex);
        }
        clearDropTarget();
      });

      listen(dragHandle, 'dragend', () => {
        block.classList.remove('ps-builder-dragging');
        draggedNodeId = '';
        clearDropTarget();
      });
    });

    previewDragCleanupRef.current = () => {
      // Do not read scroll here. On srcDoc replacement this cleanup can run
      // after the iframe already points at the new document (which starts at 0),
      // accidentally wiping the real saved position.
      previewWindow.removeEventListener('scroll', rememberScrollPosition);
      clearDropTarget();
      listeners.forEach(([element, type, handler]) => element.removeEventListener(type, handler));
      blocks.forEach((block) => {
        block.classList.remove(
          'ps-builder-draggable',
          'ps-builder-dragging',
          'ps-builder-drop-before',
          'ps-builder-drop-after',
          'ps-builder-hero-resizing'
        );
        block.querySelectorAll('.ps-builder-preview-delete, .ps-builder-preview-drag-handle, .ps-builder-hero-resize, .ps-builder-hero-resize-label').forEach((button) => button.remove());
      });
      style.remove();
    };
  }, [currentBuilderWidgets, removeWidgetFromSlot, reorderWidgetInSlot, selectedBuilderSlot, setPreviewHeroHeight, setSelectedBuilderWidgetIndex, updateWidgetProps]);

  const installPreviewInlineEditing = React.useCallback(() => {
    if (previewInlineCleanupRef.current) {
      previewInlineCleanupRef.current();
      previewInlineCleanupRef.current = null;
    }

    const frame = previewFrameRef.current;
    const doc = frame?.contentDocument;
    if (!doc || typeof updateWidgetProps !== 'function') return;

    const style = doc.createElement('style');
    style.setAttribute('data-ps-builder-inline-style', 'true');
    style.textContent = [
      '[data-ps-inline-edit]{cursor:text!important;border-radius:4px;outline:1px dashed transparent;outline-offset:3px;transition:outline-color .12s ease,background .12s ease;}',
      '[data-ps-inline-edit]:hover{outline-color:rgba(195,23,29,.45)!important;}',
      '[data-ps-inline-edit]:focus{outline:2px solid #C3171D!important;background:rgba(255,255,255,.10)!important;}',
      '[data-ps-inline-edit][data-ps-inline-empty="true"]:empty:before{content:"Type here";opacity:.45;}',
    ].join('');
    doc.head?.appendChild(style);

    const cleanText = (element) => String(element?.innerText ?? element?.textContent ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r/g, '')
      .trim();

    const listeners = [];
    const listen = (element, type, handler, options) => {
      element.addEventListener(type, handler, options);
      listeners.push([element, type, handler, options]);
    };

    const setTopLevel = (widgetIndex, key, value) => {
      updateWidgetProps(selectedBuilderSlot, widgetIndex, key, value);
    };

    const setArrayField = (widgetIndex, arrayKey, itemIndex, field, value) => {
      const widget = latestBuilderWidgetsRef.current?.[widgetIndex];
      const items = Array.isArray(widget?.props?.[arrayKey]) ? [...widget.props[arrayKey]] : [];
      if (!items[itemIndex]) return;
      items[itemIndex] = { ...(items[itemIndex] || {}), [field]: value };
      updateWidgetProps(selectedBuilderSlot, widgetIndex, arrayKey, items);
    };

    const setHeroField = (widgetIndex, slideField, fallbackField, value) => {
      const widget = latestBuilderWidgetsRef.current?.[widgetIndex];
      const slides = Array.isArray(widget?.props?.slides) ? [...widget.props.slides] : [];
      if (slides.length) {
        slides[0] = { ...(slides[0] || {}), [slideField]: value };
        updateWidgetProps(selectedBuilderSlot, widgetIndex, 'slides', slides);
      } else {
        updateWidgetProps(selectedBuilderSlot, widgetIndex, fallbackField, value);
      }
    };

    const scheduleInlineCommit = (element, onValue, value) => {
      const key = element.dataset.psInlineCommitKey || `inline_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      element.dataset.psInlineCommitKey = key;
      previewInlinePendingCommitsRef.current.set(key, { onValue, value });

      const existing = previewInlineCommitTimersRef.current.get(key);
      if (existing) window.clearTimeout(existing);

      const timer = window.setTimeout(() => {
        previewInlineCommitTimersRef.current.delete(key);
        const pending = previewInlinePendingCommitsRef.current.get(key);
        previewInlinePendingCommitsRef.current.delete(key);
        if (pending) pending.onValue(pending.value);
      }, 140);
      previewInlineCommitTimersRef.current.set(key, timer);
    };

    const flushInlineCommit = (element) => {
      const key = element?.dataset?.psInlineCommitKey;
      if (!key) return;
      const timer = previewInlineCommitTimersRef.current.get(key);
      if (timer) window.clearTimeout(timer);
      previewInlineCommitTimersRef.current.delete(key);
      const pending = previewInlinePendingCommitsRef.current.get(key);
      previewInlinePendingCommitsRef.current.delete(key);
      if (pending) pending.onValue(pending.value);
    };

    const makeEditable = (element, onValue) => {
      if (!element || element.dataset.psInlineEdit === 'true') return;
      element.dataset.psInlineEdit = 'true';
      element.dataset.psInlineEmpty = cleanText(element) ? 'false' : 'true';
      element.setAttribute('contenteditable', 'true');
      element.setAttribute('spellcheck', 'true');

      listen(element, 'mousedown', (event) => {
        event.stopPropagation();
      });
      listen(element, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
      listen(element, 'focus', (event) => {
        event.stopPropagation();
        setPreviewInlineEditing(true);
        setSelectedBuilderWidgetIndex(Number(element.closest('[data-ps-node]')?.dataset?.psBuilderIndex ?? -1));
      });
      listen(element, 'keydown', (event) => {
        event.stopPropagation();
        if (event.key === 'Enter' && !event.shiftKey && !['P','DIV'].includes(element.tagName)) {
          event.preventDefault();
          element.blur();
        }
      });
      listen(element, 'input', (event) => {
        event.stopPropagation();
        const value = cleanText(element);
        element.dataset.psInlineEmpty = value ? 'false' : 'true';
        // The contenteditable DOM is already visually current. Batch the
        // expensive React/layout document write while the user is typing.
        scheduleInlineCommit(element, onValue, value);
      });
      listen(element, 'blur', (event) => {
        event.stopPropagation();
        flushInlineCommit(element);
        setPreviewInlineEditing(false);
      });
      listen(element, 'focusout', () => {
        if (!doc.activeElement?.isContentEditable) setPreviewInlineEditing(false);
      });
    };

    const editableAll = (block, selector, callback) => {
      Array.from(block.querySelectorAll(selector)).forEach((element, itemIndex) => {
        makeEditable(element, (value) => callback(value, itemIndex));
      });
    };

    Array.from(doc.querySelectorAll('[data-ps-node]')).forEach((block) => {
      const nodeId = String(block.getAttribute('data-ps-node') || '');
      const widgetIndex = latestBuilderWidgetsRef.current.findIndex((widget) => String(widget?.id || '') === nodeId);
      if (widgetIndex < 0) return;
      block.dataset.psBuilderIndex = String(widgetIndex);
      const component = latestBuilderWidgetsRef.current?.[widgetIndex]?.component;

      if (component === 'hero') {
        makeEditable(block.querySelector('.ps-hero__title'), (value) => setHeroField(widgetIndex, 'h1', 'title', value));
        makeEditable(block.querySelector('.ps-hero__subtitle'), (value) => setHeroField(widgetIndex, 'paragraph', 'subtitle', value));
        makeEditable(block.querySelector('.ps-hero__cta'), (value) => setHeroField(widgetIndex, 'button_text', 'cta_text', value));
      } else if (component === 'product_grid' || component === 'product_carousel') {
        makeEditable(block.querySelector('h3'), (value) => setTopLevel(widgetIndex, 'title', value));
      } else if (['category_grid','brand_grid','theme_grid','featured_categories'].includes(component)) {
        makeEditable(block.querySelector('.ps-section-heading h3'), (value) => setTopLevel(widgetIndex, 'heading', value));
      } else if (component === 'button_row') {
        editableAll(block, '.ps-button', (value, itemIndex) => {
          const widget = latestBuilderWidgetsRef.current?.[widgetIndex];
          if (Array.isArray(widget?.props?.buttons) && widget.props.buttons.length) {
            setArrayField(widgetIndex, 'buttons', itemIndex, 'label', value);
          } else if (itemIndex === 0) {
            setTopLevel(widgetIndex, 'label', value);
          }
        });
      } else if (component === 'rich_text') {
        makeEditable(block.querySelector('.ps-content-media__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-rich-text__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('.ps-rich-text__body p'), (value) => setTopLevel(widgetIndex, 'body', value));
        makeEditable(block.querySelector('.ps-content-media__cta'), (value) => setTopLevel(widgetIndex, 'cta_label', value));
      } else if (component === 'cards_grid') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-info-card__icon', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'icon', value));
        editableAll(block, '.ps-info-card__title', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'title', value));
        editableAll(block, '.ps-info-card > p', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'body', value));
        editableAll(block, '.ps-info-card__link', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'link_label', value.replace(/\s*→\s*$/, '')));
      } else if (component === 'stats') {
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-stat__value', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'value', value));
        editableAll(block, '.ps-stat__label', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'label', value));
      } else if (component === 'testimonials') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-testimonial__quote', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'quote', value.replace(/^“|”$/g, '')));
        editableAll(block, '.ps-testimonial__name', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'name', value));
        editableAll(block, '.ps-testimonial__role', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'role', value));
      } else if (component === 'faq') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-faq__question > span:first-child', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'question', value));
        editableAll(block, '.ps-faq__answer p', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'answer', value));
      } else if (component === 'logo_cloud') {
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-logo-item > span', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'name', value));
      } else if (component === 'cta') {
        makeEditable(block.querySelector('.ps-cta__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-cta__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('.ps-cta__body p'), (value) => setTopLevel(widgetIndex, 'body', value));
        const actions = block.querySelectorAll('.ps-cta__actions .ps-button');
        makeEditable(actions[0], (value) => setTopLevel(widgetIndex, 'primary_label', value));
        makeEditable(actions[1], (value) => setTopLevel(widgetIndex, 'secondary_label', value));
      } else if (component === 'gallery') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-gallery__caption', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'caption', value));
      } else if (component === 'video') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('.ps-section-intro__description p'), (value) => setTopLevel(widgetIndex, 'body', value));
        makeEditable(block.querySelector('.ps-video__caption'), (value) => setTopLevel(widgetIndex, 'caption', value));
      } else if (component === 'team') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-team-card__name', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'name', value));
        editableAll(block, '.ps-team-card__role', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'role', value));
        editableAll(block, '.ps-team-card__content > p', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'bio', value));
      } else if (component === 'timeline') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        editableAll(block, '.ps-timeline__marker', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'label', value));
        editableAll(block, '.ps-timeline__title', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'title', value));
        editableAll(block, '.ps-timeline__body p', (value, itemIndex) => setArrayField(widgetIndex, 'items', itemIndex, 'body', value));
      } else if (component === 'comparison') {
        makeEditable(block.querySelector('.ps-section-intro__subheading'), (value) => setTopLevel(widgetIndex, 'subheading', value));
        makeEditable(block.querySelector('.ps-section-intro__title'), (value) => setTopLevel(widgetIndex, 'title', value));
      } else if (component === 'form') {
        makeEditable(block.querySelector('.ps-form__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('.ps-form__description'), (value) => setTopLevel(widgetIndex, 'description', value));
        makeEditable(block.querySelector('.ps-form .ps-button'), (value) => setTopLevel(widgetIndex, 'submit_label', value));
      } else if (component === 'contact_block') {
        makeEditable(block.querySelector('.ps-contact-block__title'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('.ps-contact-block > p'), (value) => setTopLevel(widgetIndex, 'cta', value));
        const links = block.querySelectorAll('.ps-contact-block__links > span');
        makeEditable(links[0], (value) => setTopLevel(widgetIndex, 'email', value));
        makeEditable(links[1], (value) => setTopLevel(widgetIndex, 'phone', value));
      } else {
        makeEditable(block.querySelector('h1,h2,h3'), (value) => setTopLevel(widgetIndex, 'title', value));
        makeEditable(block.querySelector('p'), (value) => setTopLevel(widgetIndex, 'body', value));
      }
    });

    previewInlineCleanupRef.current = () => {
      Array.from(doc.querySelectorAll('[data-ps-inline-edit]')).forEach((element) => flushInlineCommit(element));
      listeners.forEach(([element, type, handler, options]) => element.removeEventListener(type, handler, options));
      Array.from(doc.querySelectorAll('[data-ps-inline-edit]')).forEach((element) => {
        element.removeAttribute('contenteditable');
        element.removeAttribute('spellcheck');
        delete element.dataset.psInlineEdit;
        delete element.dataset.psInlineEmpty;
      });
      Array.from(doc.querySelectorAll('[data-ps-builder-index]')).forEach((element) => delete element.dataset.psBuilderIndex);
      style.remove();
    };
  }, [selectedBuilderSlot, setSelectedBuilderWidgetIndex, updateWidgetProps]);

  React.useEffect(() => () => {
    if (previewDragCleanupRef.current) previewDragCleanupRef.current();
    if (previewInlineCleanupRef.current) previewInlineCleanupRef.current();
    previewInlineCommitTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    previewInlineCommitTimersRef.current.clear();
    previewInlinePendingCommitsRef.current.clear();
  }, []);

  React.useEffect(() => {
    if (builderCanvasMode !== 'preview') return undefined;
    const frame = previewFrameRef.current;
    if (!frame?.contentDocument || !templateStylesPreviewSrcDoc) return undefined;

    const activePreviewElement = frame.contentDocument.activeElement;
    const isActuallyInlineEditing = Boolean(
      activePreviewElement
      && (activePreviewElement.isContentEditable || activePreviewElement?.dataset?.psInlineEdit === 'true')
    );
    // Suppress DOM replacement only while the user is genuinely typing inside
    // the iframe. A stale previewInlineEditing flag must never block inspector
    // changes from updating the live preview.
    if (isActuallyInlineEditing) return undefined;
    if (previewInlineEditing) setPreviewInlineEditing(false);

    // Page/renderer changes intentionally hard-load. Ordinary builder changes
    // patch the existing iframe DOM so scroll, selection and browser state stay put.
    const currentKey = `${selectedBuilderSlot}::${previewRendererMode}`;
    if (previewHardLoadKeyRef.current !== currentKey) return undefined;

    previewSoftPatchQueuedRef.current = true;
    const frameId = window.requestAnimationFrame(() => {
      if (!previewSoftPatchQueuedRef.current) return;
      previewSoftPatchQueuedRef.current = false;
      const patchResult = softPatchPreviewDocument(templateStylesPreviewSrcDoc);
      // Style-only updates deliberately keep the existing editor chrome and
      // event listeners mounted. Reinstall only when block content/structure
      // actually changed.
      if (patchResult.patched && patchResult.contentChanged) {
        installPreviewDragAndDrop();
        installPreviewInlineEditing();
      }
    });

    return () => {
      previewSoftPatchQueuedRef.current = false;
      window.cancelAnimationFrame(frameId);
    };
  }, [
    builderCanvasMode,
    installPreviewDragAndDrop,
    installPreviewInlineEditing,
    previewInlineEditing,
    previewRendererMode,
    selectedBuilderSlot,
    softPatchPreviewDocument,
    templateStylesPreviewSrcDoc,
  ]);

  const handlePageTemplateImport = (template, mode) => {
    const result = onImportPageTemplate?.(template.id, mode);
    if (result !== null) {
      setShowPageLayouts(false);
      setSelectedBuilderWidgetIndex(null);
      setBuilderCanvasMode('preview');
    }
  };

  const templateGalleryTemplates = useMemo(
    () => (templates || []).map((tpl) => ({
      ...tpl,
      active: String(tpl.id) === String(settings?.activeTemplate?.template_id || ''),
    })),
    [settings?.activeTemplate?.template_id, templates]
  );

  const selectWidget = (index) => {
    setSelectedBuilderWidgetIndex(index);
    setSidebarView(BUILDER_SIDEBAR_VIEWS.INSPECTOR);
  };

  const addSelectedWidget = () => {
    const nextIndex = currentBuilderWidgets.length;
    addWidgetToSlot();
    setSelectedBuilderWidgetIndex(nextIndex);
    setSidebarView(BUILDER_SIDEBAR_VIEWS.INSPECTOR);
  };

  const spacingDefault = (component, key) => {
    if (key.startsWith('margin_')) return 0;
    if (component === 'hero') return 0;
    if (key === 'padding_top' || key === 'padding_bottom') return 28;
    return 0;
  };

  const heroSlidePaddingDefault = (key) => (
    key === 'slide_padding_top' || key === 'slide_padding_bottom' ? 28 : 16
  );

  const quickSectionCategories = useMemo(
    () => ['All', ...Array.from(new Set((quickSections || []).map((section) => section.category).filter(Boolean)))],
    [quickSections]
  );
  const filteredQuickSections = useMemo(
    () => (quickSections || []).filter((section) => quickSectionCategory === 'All' || section.category === quickSectionCategory),
    [quickSectionCategory, quickSections]
  );

  const addQuickSection = (sectionId) => {
    insertQuickSection?.(sectionId, selectedBuilderSlot);
    setSelectedBuilderWidgetIndex(null);
    setSidebarView(BUILDER_SIDEBAR_VIEWS.ELEMENTS);
  };

  const elementsView = (
    <Box className="ps-builder-panel-stack">
      <Box sx={{ display: 'grid', gap: .55, maxHeight: 100, minHeight: 88 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: .75, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850, fontSize: 13 }}>Quick Sections</Typography>
            <Typography variant="caption" className="ps-builder-panel-copy" sx={{ display: 'block', lineHeight: 1.15 }}>
              Add a ready-made section.
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            value={quickSectionCategory}
            onChange={(event) => setQuickSectionCategory(event.target.value)}
            sx={{
              width: 112,
              flexShrink: 0,
              '& .MuiInputBase-root': { height: 30, fontSize: 11.5 },
              '& .MuiSelect-select': { py: .45 },
            }}
          >
            {quickSectionCategories.map((category) => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: .55,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: .25,
            pr: .25,
            minHeight: 46,
            maxHeight: 48,
            scrollSnapType: 'x proximity',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 5 },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(100,116,139,.35)',
              borderRadius: 999,
            },
          }}
        >
          {filteredQuickSections.map((section) => (
            <Button
              key={section.id}
              size="small"
              variant="outlined"
              onClick={() => addQuickSection(section.id)}
              title={section.description || section.label}
              sx={{
                flex: '0 0 auto',
                minWidth: 118,
                maxWidth: 150,
                height: 42,
                px: .8,
                py: .45,
                textTransform: 'none',
                justifyContent: 'flex-start',
                alignItems: 'center',
                textAlign: 'left',
                lineHeight: 1.08,
                fontSize: 11.5,
                fontWeight: 800,
                borderColor: 'rgba(15,23,42,.12)',
                color: '#0f172a',
                background: '#fff',
                scrollSnapAlign: 'start',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'rgba(195,23,29,.45)',
                  background: 'rgba(195,23,29,.035)',
                },
              }}
            >
              <Box component="span" sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {section.label}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    mt: .1,
                    color: '#94a3b8',
                    fontSize: 9.5,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  + Add section
                </Box>
              </Box>
            </Button>
          ))}
        </Box>
      </Box>

      <Divider />

      <Typography sx={{ fontWeight: 850 }}>Individual Blocks</Typography>

      <TextField
        select
        size="small"
        label="Block type"
        value={widgetDraft.type}
        onChange={(event) => setWidgetDraft((prev) => ({ ...prev, type: event.target.value }))}
        fullWidth
      >
        {widgetLibrary.map((widget) => (
          <MenuItem key={widget.key} value={widget.key}>{widget.label}</MenuItem>
        ))}
      </TextField>

      <Box className="ps-builder-element-list">
        {widgetLibrary.map((widget) => {
          const meta = getWidgetMeta(widget.key);
          const ElementIcon = meta.icon;
          const active = widgetDraft.type === widget.key;
          return (
            <Box
              key={widget.key}
              className={`ps-builder-element-card${active ? ' is-active' : ''}`}
              onClick={() => setWidgetDraft((prev) => ({ ...prev, type: widget.key }))}
            >
              <Box className="ps-builder-element-icon" sx={{ color: meta.accent }}>
                <ElementIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography className="ps-builder-element-title">{widget.label}</Typography>
                <Typography variant="caption" className="ps-builder-panel-copy">
                  {widgetDetails[widget.key] || 'Add this content block to your page.'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button variant="contained" fullWidth onClick={addSelectedWidget}>
        Add to {selectedBuilderSlotMeta?.label || selectedBuilderSlot}
      </Button>
    </Box>
  );

  const pagesView = (
    <Box className="ps-builder-panel-stack">
      <Box className="ps-builder-page-layout-callout">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800 }}>Build {selectedBuilderSlotMeta?.label || selectedBuilderSlot}</Typography>
          <Typography variant="caption" className="ps-builder-panel-copy">
            Add a ready-made layout to this page, then edit each block normally.
          </Typography>
        </Box>
        {canUsePageLayouts ? (
          <Button variant="contained" size="small" onClick={() => setShowPageLayouts((prev) => !prev)}>
            {showPageLayouts ? 'Back to pages' : `+ Add layout to ${selectedBuilderSlotMeta?.label || selectedBuilderSlot}`}
          </Button>
        ) : (
          <Typography variant="caption" className="ps-builder-panel-copy">
            Choose from modern promo, print, service and portfolio layouts for this page.
          </Typography>
        )}
      </Box>

      <Button variant="outlined" size="small" onClick={addShowcasePage}>+ Add page</Button>

      {showPageLayouts && canUsePageLayouts ? (
        <PageTemplateGallery
          selectedPageType={pageTemplateType}
          onImport={handlePageTemplateImport}
          allowedTypes={pageTemplateAllowedTypes}
          compact
        />
      ) : (
        <>
          <Typography variant="overline" className="ps-builder-section-label">Pages</Typography>
          {builderSlots.map((slot) => {
            const meta = slotMeta[slot] || {};
            const SlotIcon = meta.icon || ViewQuiltRoundedIcon;
            const active = selectedBuilderSlot === slot;
            const count = Array.isArray(layoutBuilder?.slots?.[slot]) ? layoutBuilder.slots[slot].length : 0;
            return (
              <Box
                key={slot}
                className={`ps-builder-page-row${active ? ' is-active' : ''}`}
                onClick={() => {
                  setSelectedBuilderSlot(slot);
                  setSelectedBuilderWidgetIndex(null);
                  setShowPageLayouts(false);
                }}
              >
                <Box className="ps-builder-page-row__icon" sx={{ background: meta.tint || 'rgba(148,163,184,.12)' }}>
                  <SlotIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography className="ps-builder-page-row__title">{meta.label || slot}</Typography>
                  <Typography variant="caption" className="ps-builder-panel-copy">
                    {count} block{count === 1 ? '' : 's'}
                  </Typography>
                </Box>
                {active ? <Chip size="small" label="Editing" /> : null}
              </Box>
            );
          })}
          {(() => {
            const page = (showcasePages || []).find((item) => item?.slot === selectedBuilderSlot);
            if (!page) return null;
            return (
              <Box sx={{ mt: 1, display: 'grid', gap: 1 }}>
                <Divider />
                <Typography variant="overline" className="ps-builder-section-label">Page settings</Typography>
                <TextField size="small" label="Page title" value={page.title || ''} onChange={(event) => updateShowcasePage?.(selectedBuilderSlot, { title: event.target.value })} />
                <TextField size="small" label="URL slug" value={page.slug || ''} onChange={(event) => updateShowcasePage?.(selectedBuilderSlot, { slug: event.target.value.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase() })} />
                <TextField
                  select
                  size="small"
                  label="Show in navigation"
                  value={page.show_in_menu === false ? 'no' : 'yes'}
                  onChange={(event) => updateShowcasePage?.(selectedBuilderSlot, { show_in_menu: event.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Front page"
                  value={page.set_front_page ? 'yes' : 'no'}
                  onChange={(event) => updateShowcasePage?.(selectedBuilderSlot, { set_front_page: event.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <Button color="error" variant="outlined" size="small" disabled={selectedBuilderSlot === 'home'} onClick={() => removeShowcasePage?.(selectedBuilderSlot)}>
                  Delete page
                </Button>
              </Box>
            );
          })()}
        </>
      )}
    </Box>
  );

  const layersView = (
    <Box className="ps-builder-panel-stack">
      {currentBuilderWidgets.length === 0 ? (
        <Typography variant="body2" className="ps-builder-panel-copy">
          This page is empty. Choose Add to place your first block.
        </Typography>
      ) : currentBuilderWidgets.map((widget, index) => {
        const meta = getWidgetMeta(widget.component);
        const LayerIcon = meta.icon;
        const active = selectedBuilderWidgetIndex === index;
        return (
          <Box
            key={`${selectedBuilderSlot}-${index}`}
            className={`ps-builder-layer-row${active ? ' is-active' : ''}`}
            onClick={() => selectWidget(index)}
          >
            <Box className="ps-builder-layer-row__main">
              <LayerIcon sx={{ color: meta.accent, fontSize: 17 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography className="ps-builder-layer-row__title">{getWidgetLabel(widget.component)}</Typography>
                <Typography variant="caption" className="ps-builder-panel-copy">Block {index + 1}</Typography>
              </Box>
            </Box>
            <Box className="ps-builder-layer-row__actions">
              <IconButton size="small" onClick={(event) => { event.stopPropagation(); moveWidgetInSlot(selectedBuilderSlot, index, 'up'); }}>
                <ArrowUpwardRoundedIcon fontSize="inherit" />
              </IconButton>
              <IconButton size="small" onClick={(event) => { event.stopPropagation(); moveWidgetInSlot(selectedBuilderSlot, index, 'down'); }}>
                <ArrowDownwardRoundedIcon fontSize="inherit" />
              </IconButton>
              <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); removeWidgetFromSlot(selectedBuilderSlot, index); }}>
                <DeleteOutlineRoundedIcon fontSize="inherit" />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const inspectorView = !selectedBuilderWidget ? (
    <Box className="ps-builder-empty-panel">
      <Typography sx={{ fontWeight: 800 }}>Nothing selected</Typography>
      <Typography variant="body2" className="ps-builder-panel-copy">
        Select a block on the page or in Page Structure to edit it.
      </Typography>
    </Box>
  ) : (
    <Box className="ps-builder-panel-stack">
      <Box className="ps-builder-selected-widget-heading">
        {(() => {
          const SelectedIcon = selectedBuilderWidgetMeta.icon;
          return <SelectedIcon sx={{ color: selectedBuilderWidgetMeta.accent, fontSize: 20 }} />;
        })()}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800 }}>{getWidgetLabel(selectedBuilderWidget.component)}</Typography>
          <Typography variant="caption" className="ps-builder-panel-copy">Block {selectedBuilderWidgetIndex + 1}</Typography>
        </Box>
      </Box>

      {selectedBuilderWidgetPresets.length ? (
        <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>
          {selectedBuilderWidgetPresets.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              variant="outlined"
              onClick={() => applyWidgetTemplatePreset(selectedBuilderSlot, selectedBuilderWidgetIndex, preset.props)}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
      ) : null}

      {selectedBuilderWidget.component === 'hero' ? (
        <>
          <TextField
            size="small"
            type="number"
            label="Banner height"
            value={selectedBuilderWidget?.props?.min_height || 460}
            inputProps={{ min: 220, max: 1200, step: 10 }}
            helperText="220–1200px. You can also drag the bottom edge of the banner in Preview."
            onChange={(event) => {
              const nextHeight = Math.max(220, Math.min(1200, Number(event.target.value || 220)));
              const nodeId = String(selectedBuilderWidget?.id || '');
              setPreviewHeroHeight(nodeId, nextHeight);
              updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'min_height', nextHeight);
            }}
          />
          <TextField
            select
            size="small"
            label="Hero width"
            value={selectedBuilderWidget?.props?.width_mode || 'full_bleed'}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'width_mode', event.target.value)}
            helperText={selectedBuilderWidget?.props?.width_mode === 'contained'
              ? 'Background and content stay inside the site grid.'
              : 'Background spans edge to edge while text stays aligned to the site grid.'}
          >
            <MenuItem value="contained">Contained</MenuItem>
            <MenuItem value="full_bleed">Full width background</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Autoplay"
            value={selectedBuilderWidget?.props?.autoplay === false ? 'off' : 'on'}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'autoplay', event.target.value === 'on')}
          >
            <MenuItem value="on">On</MenuItem>
            <MenuItem value="off">Off</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Slide duration (ms)"
            value={selectedBuilderWidget?.props?.autoplay_ms || 6500}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'autoplay_ms', Number(event.target.value || 6500))}
            inputProps={{ min: 2500, max: 20000, step: 500 }}
          />
        </>
      ) : null}

      {['hero', 'hero_split', 'rich_text', 'product_grid', 'product_carousel', 'featured_categories', 'category_grid', 'brand_grid', 'theme_grid'].includes(selectedBuilderWidget.component) ? (
        <TextField
          size="small"
          label="Heading"
          value={
            selectedBuilderWidget.component === 'hero' && Array.isArray(selectedBuilderWidget?.props?.slides) && selectedBuilderWidget.props.slides.length
              ? (selectedBuilderWidget.props.slides[0]?.h1 || '')
              : (selectedBuilderWidget?.props?.title || selectedBuilderWidget?.props?.headline || '')
          }
          onChange={(event) => {
            const value = event.target.value;
            if (selectedBuilderWidget.component === 'hero' && Array.isArray(selectedBuilderWidget?.props?.slides) && selectedBuilderWidget.props.slides.length) {
              updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, 0, 'h1', value);
            } else {
              updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'title', value);
            }
          }}
        />
      ) : null}

      {['hero', 'hero_split', 'rich_text'].includes(selectedBuilderWidget.component) ? (
        <TextField
          size="small"
          label="Supporting text"
          multiline
          minRows={2}
          value={
            selectedBuilderWidget.component === 'hero' && Array.isArray(selectedBuilderWidget?.props?.slides) && selectedBuilderWidget.props.slides.length
              ? (selectedBuilderWidget.props.slides[0]?.paragraph || '')
              : (selectedBuilderWidget?.props?.subtitle || selectedBuilderWidget?.props?.body || '')
          }
          onChange={(event) => {
            const value = event.target.value;
            if (selectedBuilderWidget.component === 'hero' && Array.isArray(selectedBuilderWidget?.props?.slides) && selectedBuilderWidget.props.slides.length) {
              updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, 0, 'paragraph', value);
            } else {
              updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'subtitle', value);
            }
          }}
        />
      ) : null}

      <TextField
        size="small"
        type="color"
        label="Text color"
        value={selectedBuilderWidget?.props?.text_color || '#0f172a'}
        onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'text_color', event.target.value)}
        InputLabelProps={{ shrink: true }}
        helperText="Sets the primary text color for this block."
      />

      {['featured_categories', 'category_grid', 'brand_grid', 'theme_grid'].includes(selectedBuilderWidget.component) ? (
        <TextField
          size="small"
          label="Number of items"
          value={selectedBuilderWidget?.props?.limit || ''}
          onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'limit', event.target.value)}
        />
      ) : null}

      {['product_grid', 'product_carousel'].includes(selectedBuilderWidget.component) ? (
        <Box className="ps-builder-panel-stack">
          <Typography variant="caption" sx={{ fontWeight: 800 }}>Layout</Typography>
          <Box className="ps-builder-responsive-fields">
            <TextField select size="small" label="Desktop columns" value={selectedBuilderWidget?.props?.columns_desktop ?? 4} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_desktop', Number(event.target.value))}>
              {[2, 3, 4, 5, 6].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Tablet columns" value={selectedBuilderWidget?.props?.columns_tablet ?? 3} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_tablet', Number(event.target.value))}>
              {[1, 2, 3, 4].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Mobile columns" value={selectedBuilderWidget?.props?.columns_mobile ?? 2} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_mobile', Number(event.target.value))}>
              {[1, 2].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>Products</Typography>
          <TextField select size="small" label="Source" value={selectedBuilderWidget?.props?.query_source || 'all'} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'query_source', event.target.value)}>
            <MenuItem value="all">All products</MenuItem>
            <MenuItem value="promoworld">PromoWorld</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </TextField>
          <TextField size="small" label="Supplier" value={selectedBuilderWidget?.props?.query_supplier || ''} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'query_supplier', event.target.value)} />
          <TextField size="small" label="Search" value={selectedBuilderWidget?.props?.query_search || ''} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'query_search', event.target.value)} />
          <TextField size="small" label="Specific product IDs" helperText="Comma-separated product IDs" value={selectedBuilderWidget?.props?.query_product_ids || ''} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'query_product_ids', event.target.value)} />
          <TextField select size="small" label="Sort" value={selectedBuilderWidget?.props?.query_sort || 'default'} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'query_sort', event.target.value)}>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="name_asc">Name A–Z</MenuItem>
            <MenuItem value="name_desc">Name Z–A</MenuItem>
          </TextField>
          <TextField size="small" type="number" label="Products shown" value={selectedBuilderWidget?.props?.page_size ?? 24} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'page_size', Number(event.target.value))} />
        </Box>
      ) : null}

      {selectedBuilderWidget.component === 'contact_block' ? (
        <TextField
          size="small"
          label="Contact message"
          value={selectedBuilderWidget?.props?.cta || ''}
          onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'cta', event.target.value)}
        />
      ) : null}

      {['category_grid', 'brand_grid', 'theme_grid'].includes(selectedBuilderWidget.component) ? (
        <Box className="ps-builder-panel-stack">
          <TextField
            size="small"
            label="Heading"
            value={selectedBuilderWidget?.props?.heading || selectedBuilderWidget?.props?.title || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'heading', event.target.value)}
          />
          <TextField
            select
            size="small"
            label="Show items by"
            value={selectedBuilderWidget?.props?.taxonomy || 'product_category'}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'taxonomy', event.target.value)}
          >
            <MenuItem value="product_category">Product Category</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
            <MenuItem value="theme">Theme</MenuItem>
            <MenuItem value="collection">Collection</MenuItem>
          </TextField>
          <Box className="ps-builder-responsive-fields">
            <TextField size="small" type="number" label="Desktop" value={selectedBuilderWidget?.props?.columns_desktop ?? 8} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_desktop', Number(event.target.value || 8))} />
            <TextField size="small" type="number" label="Tablet" value={selectedBuilderWidget?.props?.columns_tablet ?? 4} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_tablet', Number(event.target.value || 4))} />
            <TextField size="small" type="number" label="Mobile" value={selectedBuilderWidget?.props?.columns_mobile ?? 2} onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_mobile', Number(event.target.value || 2))} />
          </Box>
        </Box>
      ) : null}

      {selectedBuilderWidget.component === 'cards_grid' ? (
        <Box className="ps-builder-panel-stack">
          <Typography variant="caption" sx={{ fontWeight: 800 }}>Section content</Typography>
          <TextField
            size="small"
            label="Subheading"
            value={selectedBuilderWidget?.props?.subheading || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'subheading', event.target.value)}
          />
          <TextField
            size="small"
            label="Heading"
            value={selectedBuilderWidget?.props?.title || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'title', event.target.value)}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            label="Description"
            value={selectedBuilderWidget?.props?.description || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'description', event.target.value)}
          />

          <Typography variant="caption" sx={{ fontWeight: 800 }}>Card layout</Typography>
          <TextField
            select
            size="small"
            label="Card style"
            value={selectedBuilderWidget?.props?.card_style || 'elevated'}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'card_style', event.target.value)}
          >
            <MenuItem value="elevated">Elevated</MenuItem>
            <MenuItem value="outlined">Outlined</MenuItem>
            <MenuItem value="filled">Filled</MenuItem>
            <MenuItem value="minimal">Minimal</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Content type"
            value={selectedBuilderWidget?.props?.variant || 'services'}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'variant', event.target.value)}
          >
            <MenuItem value="services">Services</MenuItem>
            <MenuItem value="features">Features</MenuItem>
            <MenuItem value="benefits">Benefits</MenuItem>
            <MenuItem value="process">Process / Steps</MenuItem>
          </TextField>
          <Box className="ps-builder-responsive-fields">
            <TextField
              select
              size="small"
              label="Desktop columns"
              value={selectedBuilderWidget?.props?.columns_desktop ?? 3}
              onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_desktop', Number(event.target.value))}
            >
              {[1, 2, 3, 4].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Tablet columns"
              value={selectedBuilderWidget?.props?.columns_tablet ?? 2}
              onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_tablet', Number(event.target.value))}
            >
              {[1, 2, 3, 4].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Mobile columns"
              value={selectedBuilderWidget?.props?.columns_mobile ?? 1}
              onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'columns_mobile', Number(event.target.value))}
            >
              {[1, 2].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </Box>

          <Box className="ps-builder-subpanel">
            <Box className="ps-builder-subpanel__heading">
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  Cards ({Array.isArray(selectedBuilderWidget?.props?.items) ? selectedBuilderWidget.props.items.length : 0})
                </Typography>
                <Typography variant="caption" className="ps-builder-panel-copy" sx={{ display: 'block' }}>
                  Edit the content shown inside each card.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const items = Array.isArray(selectedBuilderWidget?.props?.items) ? selectedBuilderWidget.props.items : [];
                  updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', [
                    ...items,
                    {
                      icon: String(items.length + 1).padStart(2, '0'),
                      title: 'New card',
                      body: 'Add card content here.',
                      link_label: '',
                      link_url: '',
                    },
                  ]);
                }}
              >
                + Add card
              </Button>
            </Box>

            {(Array.isArray(selectedBuilderWidget?.props?.items) ? selectedBuilderWidget.props.items : []).map((item, itemIndex) => (
              <Box
                key={`cards-grid-item-${selectedBuilderWidget?.id || selectedBuilderWidgetIndex}-${itemIndex}`}
                className="ps-builder-slide-card"
                sx={{ display: 'grid', gap: .75 }}
              >
                <Box className="ps-builder-subpanel__heading">
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>Card {itemIndex + 1}</Typography>
                  <Box sx={{ display: 'flex', gap: .25, alignItems: 'center' }}>
                    <Tooltip title="Move card up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={itemIndex === 0}
                          onClick={() => {
                            const items = [...(selectedBuilderWidget?.props?.items || [])];
                            const [moved] = items.splice(itemIndex, 1);
                            items.splice(itemIndex - 1, 0, moved);
                            updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                          }}
                        >
                          <ArrowUpwardRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move card down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={itemIndex >= (selectedBuilderWidget?.props?.items?.length || 0) - 1}
                          onClick={() => {
                            const items = [...(selectedBuilderWidget?.props?.items || [])];
                            const [moved] = items.splice(itemIndex, 1);
                            items.splice(itemIndex + 1, 0, moved);
                            updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                          }}
                        >
                          <ArrowDownwardRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete card">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const items = (selectedBuilderWidget?.props?.items || []).filter((_entry, index) => index !== itemIndex);
                          updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                        }}
                      >
                        <DeleteOutlineRoundedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <TextField
                  size="small"
                  label="Icon / number"
                  value={item?.icon || ''}
                  onChange={(event) => {
                    const items = [...(selectedBuilderWidget?.props?.items || [])];
                    items[itemIndex] = { ...(items[itemIndex] || {}), icon: event.target.value };
                    updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                  }}
                  helperText="Use a short icon label, emoji, number, or leave blank."
                />
                <TextField
                  size="small"
                  label="Card title"
                  value={item?.title || ''}
                  onChange={(event) => {
                    const items = [...(selectedBuilderWidget?.props?.items || [])];
                    items[itemIndex] = { ...(items[itemIndex] || {}), title: event.target.value };
                    updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                  }}
                />
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  label="Card description"
                  value={item?.body || ''}
                  onChange={(event) => {
                    const items = [...(selectedBuilderWidget?.props?.items || [])];
                    items[itemIndex] = { ...(items[itemIndex] || {}), body: event.target.value };
                    updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                  }}
                />
                <TextField
                  size="small"
                  label="Link label"
                  value={item?.link_label || ''}
                  onChange={(event) => {
                    const items = [...(selectedBuilderWidget?.props?.items || [])];
                    items[itemIndex] = { ...(items[itemIndex] || {}), link_label: event.target.value };
                    updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                  }}
                  helperText="Optional. Leave blank for no card link."
                />
                <TextField
                  size="small"
                  label="Link URL"
                  value={item?.link_url || ''}
                  onChange={(event) => {
                    const items = [...(selectedBuilderWidget?.props?.items || [])];
                    items[itemIndex] = { ...(items[itemIndex] || {}), link_url: event.target.value };
                    updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'items', items);
                  }}
                  placeholder="/services"
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {selectedBuilderWidget.component === 'hero' ? (
        <Box className="ps-builder-subpanel">
          <Box className="ps-builder-subpanel__heading">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              Banner slides ({Array.isArray(selectedBuilderWidget?.props?.slides) ? selectedBuilderWidget.props.slides.length : 0}/10)
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => addHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex)}
              disabled={Array.isArray(selectedBuilderWidget?.props?.slides) && selectedBuilderWidget.props.slides.length >= 10}
            >
              Add
            </Button>
          </Box>
          {(Array.isArray(selectedBuilderWidget?.props?.slides) ? selectedBuilderWidget.props.slides : []).map((slide, slideIndex) => (
            <Box className="ps-builder-slide-card" key={`${selectedBuilderWidgetIndex}-${slideIndex}`}>
              <Box className="ps-builder-subpanel__heading">
                <Typography variant="caption" sx={{ fontWeight: 800 }}>Slide {slideIndex + 1}</Typography>
                <Button size="small" color="error" onClick={() => removeHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex)}>Remove</Button>
              </Box>
              <Box sx={{ display: 'grid', gap: .65 }}>
                <input
                  ref={slideBackgroundFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/avif"
                  hidden
                  onChange={handleSlideBackgroundFileSelected}
                />
                <Box sx={{ display: 'flex', gap: .65, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Background image URL"
                    value={slide?.background_url || ''}
                    onChange={(event) => updateHeroSlide(
                      selectedBuilderSlot,
                      selectedBuilderWidgetIndex,
                      slideIndex,
                      'background_url',
                      event.target.value
                    )}
                    fullWidth
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={slideBackgroundUploadingIndex === slideIndex || typeof onUploadShowcaseMedia !== 'function'}
                    onClick={() => {
                      setPendingSlideBackgroundIndex(slideIndex);
                      slideBackgroundFileInputRef.current?.click();
                    }}
                    sx={{ minWidth: 84, height: 40, flexShrink: 0 }}
                  >
                    {slideBackgroundUploadingIndex === slideIndex ? 'Uploading…' : 'Browse'}
                  </Button>
                </Box>
                {slide?.background_url ? (
                  <Box
                    component="img"
                    src={slide.background_url}
                    alt={`Slide ${slideIndex + 1} background preview`}
                    sx={{
                      width: '100%',
                      maxHeight: 110,
                      objectFit: 'cover',
                      borderRadius: 1.25,
                      border: '1px solid rgba(148,163,184,.22)',
                      background: '#f8fafc',
                    }}
                  />
                ) : null}
                <Box className="ps-builder-responsive-fields">
                  <TextField
                    select
                    size="small"
                    label="Background size"
                    value={slide?.background_size || 'cover'}
                    onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'background_size', event.target.value)}
                  >
                    <MenuItem value="cover">Cover</MenuItem>
                    <MenuItem value="contain">Contain</MenuItem>
                    <MenuItem value="auto">Auto</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Background position"
                    value={slide?.background_position || 'center center'}
                    onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'background_position', event.target.value)}
                  >
                    <MenuItem value="center center">Center</MenuItem>
                    <MenuItem value="center top">Top</MenuItem>
                    <MenuItem value="center bottom">Bottom</MenuItem>
                    <MenuItem value="left center">Left</MenuItem>
                    <MenuItem value="right center">Right</MenuItem>
                    <MenuItem value="left top">Top left</MenuItem>
                    <MenuItem value="right top">Top right</MenuItem>
                    <MenuItem value="left bottom">Bottom left</MenuItem>
                    <MenuItem value="right bottom">Bottom right</MenuItem>
                  </TextField>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Slide content padding (px)</Typography>
                <Box className="ps-builder-responsive-fields">
                  {[
                    ['slide_padding_top', 'Top'],
                    ['slide_padding_right', 'Right'],
                    ['slide_padding_bottom', 'Bottom'],
                    ['slide_padding_left', 'Left'],
                  ].map(([key, label]) => (
                    <TextField
                      key={key}
                      size="small"
                      type="number"
                      label={label}
                      value={slide?.[key] ?? heroSlidePaddingDefault(key)}
                      onChange={(event) => updateHeroSlide(
                        selectedBuilderSlot,
                        selectedBuilderWidgetIndex,
                        slideIndex,
                        key,
                        event.target.value === '' ? '' : Number(event.target.value)
                      )}
                      inputProps={{ min: 0, max: 600, step: 1 }}
                      helperText={label === 'Top' ? 'Defaults: 28px top/bottom, 16px left/right. Set any side to 0 to remove it.' : undefined}
                    />
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setStockMediaSlideIndex(slideIndex)}
                  disabled={!instanceUniqueId}
                >
                  Choose free stock image
                </Button>
                {slide?.stock_credit ? (
                  <Typography variant="caption" color="text.secondary">{slide.stock_credit}</Typography>
                ) : null}
              </Box>
              <TextField size="small" label="Headline" value={slide?.h1 || ''} onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'h1', event.target.value)} />
              <TextField size="small" multiline minRows={2} label="Paragraph" value={slide?.paragraph || ''} onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'paragraph', event.target.value)} />
              <Box className="ps-builder-responsive-fields">
                <TextField
                  size="small"
                  type="color"
                  label="Headline color"
                  value={slide?.headline_text_color || slide?.text_color || selectedBuilderWidget?.props?.text_color || '#ffffff'}
                  onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'headline_text_color', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="color"
                  label="Supporting text color"
                  value={slide?.supporting_text_color || slide?.text_color || selectedBuilderWidget?.props?.text_color || '#ffffff'}
                  onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'supporting_text_color', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <TextField size="small" label="Button text" value={slide?.button_text || ''} onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'button_text', event.target.value)} />
              <TextField size="small" label="Button URL" value={slide?.button_url || ''} onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'button_url', event.target.value)} />
              <TextField
                select
                size="small"
                label="Button size"
                value={slide?.button_size || 'medium'}
                onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'button_size', event.target.value)}
                helperText="Hero buttons remain inline, not full width."
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Overlay opacity"
                value={slide?.background_overlay_opacity ?? 0.45}
                inputProps={{ min: 0, max: 0.95, step: 0.05 }}
                onChange={(event) => updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'background_overlay_opacity', Number(event.target.value || 0))}
              />
            </Box>
          ))}
        </Box>
      ) : null}

      {selectedBuilderWidget.component === 'button_row' ? (
        <Box className="ps-builder-panel-stack">
          <TextField
            size="small"
            label="Button label"
            value={selectedBuilderWidget?.props?.label || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'label', event.target.value)}
          />
          <TextField
            size="small"
            label="Button URL"
            value={selectedBuilderWidget?.props?.url || ''}
            onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'url', event.target.value)}
          />
        </Box>
      ) : null}

      <Box
        component="details"
        className="ps-builder-advanced"
        sx={{
          border: '1px solid rgba(15,23,42,.12)',
          borderRadius: 1.5,
          background: '#fff',
          overflow: 'hidden',
          '&[open] > summary': { borderBottom: '1px solid rgba(15,23,42,.1)' },
        }}
      >
        <Box
          component="summary"
          sx={{
            px: 1,
            py: .9,
            cursor: 'pointer',
            listStyle: 'none',
            fontWeight: 800,
            fontSize: 12,
            color: '#0f172a',
            '&::-webkit-details-marker': { display: 'none' },
          }}
        >
          Advanced
          <Typography component="span" variant="caption" className="ps-builder-panel-copy" sx={{ ml: .75 }}>
            spacing, background, border & radius
          </Typography>
        </Box>

        <Box sx={{ p: 1, display: 'grid', gap: 1.2 }}>
          <Box className="ps-builder-panel-stack">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Spacing</Typography>
            <Typography variant="caption" className="ps-builder-panel-copy">
              Leave left/right margin blank to keep the centered content column. Enter a value, including 0, to override it.
            </Typography>

            <Typography variant="caption" sx={{ fontWeight: 700 }}>Margin (px)</Typography>
            <Box className="ps-builder-responsive-fields">
              {[
                ['margin_top', 'Top'],
                ['margin_right', 'Right'],
                ['margin_bottom', 'Bottom'],
                ['margin_left', 'Left'],
              ].map(([key, label]) => (
                <TextField
                  key={key}
                  size="small"
                  type="number"
                  label={label}
                  value={selectedBuilderWidget?.props?.[key] ?? spacingDefault(selectedBuilderWidget.component, key)}
                  onChange={(event) => updateWidgetProps(
                    selectedBuilderSlot,
                    selectedBuilderWidgetIndex,
                    key,
                    event.target.value === '' ? '' : Number(event.target.value)
                  )}
                  inputProps={{ min: -400, max: 400, step: 1 }}
                />
              ))}
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 700 }}>Padding (px)</Typography>
            <Box className="ps-builder-responsive-fields">
              {[
                ['padding_top', 'Top'],
                ['padding_right', 'Right'],
                ['padding_bottom', 'Bottom'],
                ['padding_left', 'Left'],
              ].map(([key, label]) => (
                <TextField
                  key={key}
                  size="small"
                  type="number"
                  label={label}
                  value={selectedBuilderWidget?.props?.[key] ?? ''}
                  onChange={(event) => updateWidgetProps(
                    selectedBuilderSlot,
                    selectedBuilderWidgetIndex,
                    key,
                    event.target.value === '' ? '' : Number(event.target.value)
                  )}
                  inputProps={{ min: 0, max: 600, step: 1 }}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          <Box className="ps-builder-panel-stack">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Typography</Typography>
            <TextField
              select
              size="small"
              label="Font family override"
              value={selectedBuilderWidget?.props?.font_family || ''}
              onChange={(event) => updateWidgetProps(selectedBuilderSlot, selectedBuilderWidgetIndex, 'font_family', event.target.value)}
              helperText="Leave as Site default to inherit the global Site Styles font."
            >
              <MenuItem value="">Site default</MenuItem>
              {SITE_FONT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Box>

          <Divider />

          <Box className="ps-builder-panel-stack">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Background</Typography>
            <TextField
              size="small"
              label="Background color"
              placeholder="transparent or #ffffff"
              value={selectedBuilderWidget?.props?.background_color ?? '#ffffff'}
              onChange={(event) => updateWidgetProps(
                selectedBuilderSlot,
                selectedBuilderWidgetIndex,
                'background_color',
                event.target.value
              )}
              helperText="Use transparent to remove the template background."
            />
          </Box>

          <Divider />

          <Box className="ps-builder-panel-stack">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Border</Typography>
            <Typography variant="caption" className="ps-builder-panel-copy">
              Override each side independently.
            </Typography>

            {['top', 'right', 'bottom', 'left'].map((side) => {
              const title = side.charAt(0).toUpperCase() + side.slice(1);
              const legacyWidth = selectedBuilderWidget?.props?.border_width ?? 1;
              const legacyStyle = selectedBuilderWidget?.props?.border_style ?? 'solid';
              const legacyColor = selectedBuilderWidget?.props?.border_color ?? '#e2e8f0';
              return (
                <Box key={side} sx={{ display: 'grid', gap: .55 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{title}</Typography>
                  <Box className="ps-builder-responsive-fields">
                    <TextField
                      size="small"
                      type="number"
                      label="Width"
                      value={selectedBuilderWidget?.props?.[`border_${side}_width`] ?? legacyWidth}
                      onChange={(event) => updateWidgetProps(
                        selectedBuilderSlot,
                        selectedBuilderWidgetIndex,
                        `border_${side}_width`,
                        event.target.value === '' ? '' : Number(event.target.value)
                      )}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Style"
                      value={selectedBuilderWidget?.props?.[`border_${side}_style`] ?? legacyStyle}
                      onChange={(event) => updateWidgetProps(
                        selectedBuilderSlot,
                        selectedBuilderWidgetIndex,
                        `border_${side}_style`,
                        event.target.value
                      )}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="solid">Solid</MenuItem>
                      <MenuItem value="dashed">Dashed</MenuItem>
                      <MenuItem value="dotted">Dotted</MenuItem>
                      <MenuItem value="double">Double</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="Color"
                      value={selectedBuilderWidget?.props?.[`border_${side}_color`] ?? legacyColor}
                      onChange={(event) => updateWidgetProps(
                        selectedBuilderSlot,
                        selectedBuilderWidgetIndex,
                        `border_${side}_color`,
                        event.target.value
                      )}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Divider />

          <Box className="ps-builder-panel-stack">
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Corner radius (px)</Typography>
            <Box className="ps-builder-responsive-fields">
              {[
                ['border_top_left_radius', 'Top left'],
                ['border_top_right_radius', 'Top right'],
                ['border_bottom_right_radius', 'Bottom right'],
                ['border_bottom_left_radius', 'Bottom left'],
              ].map(([key, label]) => (
                <TextField
                  key={key}
                  size="small"
                  type="number"
                  label={label}
                  value={selectedBuilderWidget?.props?.[key] ?? selectedBuilderWidget?.props?.border_radius ?? 14}
                  onChange={(event) => updateWidgetProps(
                    selectedBuilderSlot,
                    selectedBuilderWidgetIndex,
                    key,
                    event.target.value === '' ? '' : Number(event.target.value)
                  )}
                  inputProps={{ min: 0, max: 500, step: 1 }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider />
      <Box sx={{ display: 'flex', gap: .5, justifyContent: 'flex-end' }}>
        <IconButton size="small" onClick={() => moveWidgetInSlot(selectedBuilderSlot, selectedBuilderWidgetIndex, 'up')}>
          <ArrowUpwardRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => moveWidgetInSlot(selectedBuilderSlot, selectedBuilderWidgetIndex, 'down')}>
          <ArrowDownwardRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => removeWidgetFromSlot(selectedBuilderSlot, selectedBuilderWidgetIndex)}>
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  async function handleLogoFileSelected(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file || typeof onUploadShowcaseMedia !== 'function') return;

    setLogoUploading(true);
    try {
      const media = await onUploadShowcaseMedia(file);
      if (media?.url) {
        setTemplateForm((prev) => ({ ...prev, logoUrl: media.url }));
      }
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || 'Unable to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleSlideBackgroundFileSelected(event) {
    const file = event.target.files?.[0] || null;
    const slideIndex = pendingSlideBackgroundIndex;
    event.target.value = '';
    setPendingSlideBackgroundIndex(null);
    if (!file || slideIndex == null || typeof onUploadShowcaseMedia !== 'function') return;

    setSlideBackgroundUploadingIndex(slideIndex);
    try {
      const media = await onUploadShowcaseMedia(file);
      if (media?.url) {
        updateHeroSlide(
          selectedBuilderSlot,
          selectedBuilderWidgetIndex,
          slideIndex,
          'background_url',
          media.url
        );
        // An uploaded image is owned by this Showcase, not a stock provider.
        updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'stock_credit', '');
        updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'stock_credit_url', '');
        updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'stock_source_url', '');
        updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, slideIndex, 'stock_provider', '');
      }
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || 'Unable to upload slide background.');
    } finally {
      setSlideBackgroundUploadingIndex(null);
    }
  }

  const siteStylesSectionCard = (title, description, children) => (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        p: 1.15,
        border: '1px solid rgba(148,163,184,.22)',
        borderRadius: 1.5,
        background: '#fff',
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 850, fontSize: 13 }}>{title}</Typography>
        {description ? (
          <Typography variant="caption" className="ps-builder-panel-copy">{description}</Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  );

  const globalStylesView = (
    <Box className="ps-builder-panel-stack">
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 6,
          mx: -1.5,
          mt: -1.5,
          px: .9,
          pt: .9,
          pb: .9,
          background: 'rgba(255,255,255,.97)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(148,163,184,.16)',
        }}
      >
        <Box
          role="tablist"
          aria-label="Site style sections"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5,minmax(0,1fr))',
            gap: .45,
            p: .4,
            borderRadius: 1.4,
            background: '#f1f5f9',
            border: '1px solid rgba(148,163,184,.16)',
            overflow: 'hidden',
          }}
        >
          {[
            ['global', 'Global'],
            ['header', 'Header'],
            ['navigation', 'Nav'],
            ['footer', 'Footer'],
            ['advanced', 'More'],
          ].map(([value, label]) => {
            const active = siteStylesSection === value;
            return (
              <Button
                key={value}
                role="tab"
                aria-selected={active}
                size="small"
                onClick={() => setSiteStylesSection(value)}
                sx={{
                  minWidth: 0,
                  minHeight: 32,
                  px: .35,
                  py: .45,
                  borderRadius: 1,
                  color: active ? '#0f172a' : '#64748b',
                  background: active ? '#fff' : 'transparent',
                  boxShadow: active ? '0 1px 3px rgba(15,23,42,.10)' : 'none',
                  fontSize: 10.5,
                  fontWeight: active ? 850 : 700,
                  lineHeight: 1,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: active ? '#fff' : 'rgba(255,255,255,.62)',
                  },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ px: .15, pt: .15, pb: .15 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 850, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          {siteStylesSection === 'global' ? 'Global styles'
            : siteStylesSection === 'header' ? 'Header styles'
              : siteStylesSection === 'navigation' ? 'Navigation'
                : siteStylesSection === 'footer' ? 'Footer styles'
                  : 'Advanced'}
        </Typography>
      </Box>

      {siteStylesSection === 'global' ? (
        <>
          {siteStylesSectionCard('Brand & identity', 'Global identity used across the Showcase site.', <>
            <TextField size="small" label="Brand name" value={templateForm.brandName} onChange={(event) => setTemplateForm((prev) => ({ ...prev, brandName: event.target.value }))} />
            <Box sx={{ display: 'flex', gap: .65, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Logo URL"
                value={templateForm.logoUrl}
                onChange={(event) => setTemplateForm((prev) => ({ ...prev, logoUrl: event.target.value }))}
                fullWidth
              />
              <Button
                size="small"
                variant="outlined"
                disabled={logoUploading || typeof onUploadShowcaseMedia !== 'function'}
                onClick={() => logoFileInputRef.current?.click()}
                sx={{ minWidth: 84, height: 40, flexShrink: 0 }}
              >
                {logoUploading ? 'Uploading…' : 'Browse'}
              </Button>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/avif"
                hidden
                onChange={handleLogoFileSelected}
              />
            </Box>
            {templateForm.logoUrl ? (
              <Box component="img" src={templateForm.logoUrl} alt="Current site logo" sx={{ maxWidth: 180, maxHeight: 58, objectFit: 'contain', objectPosition: 'left center' }} />
            ) : null}
            <TextField size="small" label="Announcement text" value={templateForm.announcementText || ''} onChange={(event) => setTemplateForm((prev) => ({ ...prev, announcementText: event.target.value }))} />
          </>)}

          {siteStylesSectionCard('Typography', 'The default font for the entire site. Individual blocks can override it in Advanced.', <>
            <TextField
              select
              size="small"
              label="Site font family"
              value={templateForm.fontFamily || 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}
              onChange={(event) => setTemplateForm((prev) => ({ ...prev, fontFamily: event.target.value }))}
              helperText="All Showcase text inherits this unless a block has its own font override."
            >
              {SITE_FONT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </>)}

          {siteStylesSectionCard('Colors', 'Core brand colors inherited by buttons, accents and supported blocks.', <>
            <TextField size="small" label="Primary color" value={templateForm.primaryColor} onChange={(event) => setTemplateForm((prev) => ({ ...prev, primaryColor: event.target.value }))} />
            <TextField size="small" label="Secondary color" value={templateForm.secondaryColor || ''} onChange={(event) => setTemplateForm((prev) => ({ ...prev, secondaryColor: event.target.value }))} />
          </>)}

          {siteStylesSectionCard('Merchandising', 'Default merchandising choices used by supported catalogue sections.', <>
            <TextField size="small" label="Featured categories" value={templateForm.featuredCategories} onChange={(event) => setTemplateForm((prev) => ({ ...prev, featuredCategories: event.target.value }))} helperText="Comma separated." />
          </>)}
        </>
      ) : null}

      {siteStylesSection === 'header' ? (
        <>
          {siteStylesSectionCard('Header visibility & width', 'Control the site-wide header container.', <>
            <TextField
              select size="small" label="Header"
              value={templateForm?.siteChrome?.header?.enabled === false ? 'off' : 'on'}
              onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), enabled: event.target.value === 'on' } } }))}
            >
              <MenuItem value="on">Enabled</MenuItem><MenuItem value="off">Hidden</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Header layout width"
              value={templateForm?.siteChrome?.header?.content_width === 'full' ? 'full' : 'contained'}
              onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), content_width: event.target.value } } }))}
              helperText="Container aligns the header to the site grid. Full width stretches header content edge to edge."
            >
              <MenuItem value="contained">Container</MenuItem><MenuItem value="full">Full width</MenuItem>
            </TextField>
          </>)}

          {siteStylesSectionCard('Header typography & columns', 'Override typography and tune the desktop header balance without changing the default structure.', <>
            <TextField
              select
              size="small"
              label="Header font family"
              value={templateForm?.siteChrome?.header?.font_family || ''}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: { ...(prev.siteChrome?.header || {}), font_family: event.target.value },
                },
              }))}
              helperText="Site default inherits the global font family."
            >
              <MenuItem value="">Site default</MenuItem>
              {SITE_FONT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Header column layout"
              value={templateForm?.siteChrome?.header?.column_layout || 'balanced'}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: { ...(prev.siteChrome?.header || {}), column_layout: event.target.value },
                },
              }))}
              helperText="Balanced keeps the current layout. Presets only affect desktop."
            >
              <MenuItem value="balanced">Balanced (default)</MenuItem>
              <MenuItem value="brand_emphasis">Brand emphasis</MenuItem>
              <MenuItem value="menu_emphasis">Menu emphasis</MenuItem>
              <MenuItem value="equal">Equal thirds</MenuItem>
            </TextField>
          </>)}

          {siteStylesSectionCard('Header blocks', 'Choose which header areas exist and their order.', <>
            <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
              {[
                { key: 'brand', label: 'Brand' },
                { key: 'menu', label: 'Menu' },
                { key: 'actions', label: 'Account / Cart' },
                { key: 'search', label: 'Search' },
                { key: 'cta', label: 'CTA Button' },
              ].map(({ key: block, label }) => {
                const blocks = Array.isArray(templateForm?.siteChrome?.header?.blocks) ? templateForm.siteChrome.header.blocks : ['brand','menu','actions'];
                const exists = blocks.includes(block);
                return (
                  <Button key={block} size="small" variant={exists ? 'contained' : 'outlined'} onClick={() => {
                    const next = exists ? blocks.filter((item) => item !== block) : [...blocks, block];
                    setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), blocks: next } } }));
                  }}>
                    {exists ? '✓ ' : '+ '}{label}
                  </Button>
                );
              })}
            </Box>
            {(Array.isArray(templateForm?.siteChrome?.header?.blocks) ? templateForm.siteChrome.header.blocks : ['brand','menu','actions']).map((block, index, blocks) => (
              <Box key={block} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{block === 'actions' ? 'Account / Cart' : block === 'cta' ? 'CTA Button' : block}</Typography>
                <Box>
                  <IconButton size="small" disabled={index === 0} onClick={() => {
                    const next = [...blocks]; [next[index - 1], next[index]] = [next[index], next[index - 1]];
                    setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), blocks: next } } }));
                  }}><ArrowUpwardRoundedIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" disabled={index === blocks.length - 1} onClick={() => {
                    const next = [...blocks]; [next[index + 1], next[index]] = [next[index], next[index + 1]];
                    setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), blocks: next } } }));
                  }}><ArrowDownwardRoundedIcon fontSize="inherit" /></IconButton>
                </Box>
              </Box>
            ))}
          </>)}

          {siteStylesSectionCard('Header actions', 'Account, live product search and cart visibility in the header.', <>
            <TextField select size="small" label="Account link" value={templateForm?.siteChrome?.header?.show_account === false ? 'off' : 'on'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), show_account: event.target.value === 'on' } } }))}>
              <MenuItem value="on">Show</MenuItem><MenuItem value="off">Hide</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Product search"
              value={templateForm?.siteChrome?.header?.show_product_search === true ? 'on' : 'off'}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: {
                    ...(prev.siteChrome?.header || {}),
                    show_product_search: event.target.value === 'on',
                  },
                },
              }))}
              helperText="Off by default. Published WordPress uses FiboSearch live product results when available."
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="on">Show before cart</MenuItem>
            </TextField>
            <TextField select size="small" label="Cart link" value={templateForm?.siteChrome?.header?.show_cart === false ? 'off' : 'on'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), show_cart: event.target.value === 'on' } } }))}>
              <MenuItem value="on">Show</MenuItem><MenuItem value="off">Hide</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Cart display"
              value={templateForm?.siteChrome?.header?.cart_display === 'icon' ? 'icon' : 'label'}
              disabled={templateForm?.siteChrome?.header?.show_cart === false}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: {
                    ...(prev.siteChrome?.header || {}),
                    cart_display: event.target.value === 'icon' ? 'icon' : 'label',
                  },
                },
              }))}
              helperText="Choose a text label or compact cart icon. The item count remains visible."
            >
              <MenuItem value="label">Label</MenuItem>
              <MenuItem value="icon">Cart icon</MenuItem>
            </TextField>
            {(Array.isArray(templateForm?.siteChrome?.header?.blocks) ? templateForm.siteChrome.header.blocks : []).includes('cta') ? (
              <Box className="ps-builder-responsive-fields">
                <TextField size="small" label="CTA label" value={templateForm?.siteChrome?.header?.cta_label || 'Shop now'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), cta_label: event.target.value } } }))} />
                <TextField size="small" label="CTA URL" value={templateForm?.siteChrome?.header?.cta_url || '/catalog'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), header: { ...(prev.siteChrome?.header || {}), cta_url: event.target.value } } }))} />
              </Box>
            ) : null}
          </>)}

          {siteStylesSectionCard('Advanced header', 'Safe sizing and viewport behavior for the site header.', <>
            <TextField
              select
              size="small"
              label="Header style"
              value={templateForm?.siteChrome?.header?.background_mode === 'transparent' ? 'transparent' : 'solid'}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: {
                    ...(prev.siteChrome?.header || {}),
                    background_mode: event.target.value === 'transparent' ? 'transparent' : 'solid',
                  },
                },
              }))}
              helperText="Transparent overlay positions the header over the top of the first page section."
            >
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="transparent">Transparent overlay</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Sticky header"
              value={templateForm?.siteChrome?.header?.sticky === true ? 'on' : 'off'}
              disabled={templateForm?.siteChrome?.header?.background_mode === 'transparent'}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  header: { ...(prev.siteChrome?.header || {}), sticky: event.target.value === 'on' },
                },
              }))}
              helperText="When enabled, the header remains at the top while the page scrolls."
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="on">On</MenuItem>
            </TextField>
            <Box className="ps-builder-responsive-fields">
              <TextField
                size="small"
                type="number"
                label="Logo width (px)"
                value={templateForm?.siteChrome?.header?.logo_width_px ?? 180}
                inputProps={{ min: 40, max: 240, step: 1 }}
                onChange={(event) => {
                  const value = Math.max(40, Math.min(240, Number(event.target.value || 180)));
                  setTemplateForm((prev) => ({
                    ...prev,
                    siteChrome: {
                      ...(prev.siteChrome || {}),
                      header: { ...(prev.siteChrome?.header || {}), logo_width_px: value },
                    },
                  }));
                }}
                helperText="40–240px"
              />
              <TextField
                size="small"
                type="number"
                label="Logo max height (px)"
                value={templateForm?.siteChrome?.header?.logo_max_height_px ?? 48}
                inputProps={{ min: 24, max: 150, step: 1 }}
                onChange={(event) => {
                  const value = Math.max(24, Math.min(150, Number(event.target.value || 48)));
                  setTemplateForm((prev) => ({
                    ...prev,
                    siteChrome: {
                      ...(prev.siteChrome || {}),
                      header: { ...(prev.siteChrome?.header || {}), logo_max_height_px: value },
                    },
                  }));
                }}
                helperText="24–150px"
              />
            </Box>
            <Typography variant="caption" className="ps-builder-panel-copy">
              Logo dimensions are clamped in both Core and WordPress so an oversized value cannot stretch the header unexpectedly.
            </Typography>
          </>)}
        </>
      ) : null}

      {siteStylesSection === 'navigation' ? (
        <>
          {siteStylesSectionCard('Menu typography', 'Global styling for the primary header navigation.', <>
            <TextField
              size="small"
              type="number"
              label="Menu font size (px)"
              value={templateForm?.siteChrome?.navigation?.font_size_px ?? 14}
              inputProps={{ min: 10, max: 24, step: 1 }}
              onChange={(event) => {
                const value = Math.max(10, Math.min(24, Number(event.target.value || 14)));
                setTemplateForm((prev) => ({
                  ...prev,
                  siteChrome: {
                    ...(prev.siteChrome || {}),
                    navigation: {
                      ...(prev.siteChrome?.navigation || {}),
                      font_size_px: value,
                    },
                  },
                }));
              }}
              helperText="10–24px. Applies to the header menu links."
            />
          </>)}

          {siteStylesSectionCard('Menu assignments', 'Choose which menu is used in the header and footer.', <>
            <TextField select size="small" label="Header menu" value={templateForm?.siteChrome?.navigation?.active_menu || 'primary'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), navigation: { ...(prev.siteChrome?.navigation || {}), active_menu: event.target.value } } }))}>
              {(templateForm?.siteChrome?.navigation?.menus || []).map((menu) => <MenuItem key={menu.key} value={menu.key}>{menu.name || menu.key}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Footer menu" value={templateForm?.siteChrome?.footer?.menu_key || 'footer'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), footer: { ...(prev.siteChrome?.footer || {}), menu_key: event.target.value } } }))}>
              {(templateForm?.siteChrome?.navigation?.menus || []).map((menu) => <MenuItem key={menu.key} value={menu.key}>{menu.name || menu.key}</MenuItem>)}
            </TextField>
          </>)}

          {(templateForm?.siteChrome?.navigation?.menus || []).map((menu) => siteStylesSectionCard(
            menu.name || menu.key,
            'One item per line: Label | /url',
            <>
              <Box sx={{ display: 'flex', gap: .75, alignItems: 'center' }}>
                <TextField size="small" label="Menu name" value={menu.name || ''} onChange={(event) => {
                  const menus = (templateForm?.siteChrome?.navigation?.menus || []).map((item) => item.key === menu.key ? { ...item, name: event.target.value } : item);
                  setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), navigation: { ...(prev.siteChrome?.navigation || {}), menus } } }));
                }} fullWidth />
                <IconButton size="small" color="error" disabled={['primary','footer'].includes(menu.key)} onClick={() => {
                  const menus = (templateForm?.siteChrome?.navigation?.menus || []).filter((item) => item.key !== menu.key);
                  setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), navigation: { ...(prev.siteChrome?.navigation || {}), menus } } }));
                }}><DeleteOutlineRoundedIcon fontSize="inherit" /></IconButton>
              </Box>
              <TextField multiline minRows={3} size="small" label="Menu items" value={(menu.items || []).map((item) => `${item.label || ''} | ${item.url || ''}`).join('\n')} onChange={(event) => {
                const items = String(event.target.value || '').split('\n').map((line) => {
                  const [label, ...rest] = line.split('|'); return { label: String(label || '').trim(), url: rest.join('|').trim() };
                }).filter((item) => item.label && item.url);
                const menus = (templateForm?.siteChrome?.navigation?.menus || []).map((item) => item.key === menu.key ? { ...item, items } : item);
                setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), navigation: { ...(prev.siteChrome?.navigation || {}), mode: 'menus', menus } } }));
              }} />
            </>
          ))}
          <Button size="small" variant="outlined" onClick={() => {
            const menus = templateForm?.siteChrome?.navigation?.menus || [];
            const index = menus.length + 1;
            const key = `menu-${index}`;
            setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), navigation: { ...(prev.siteChrome?.navigation || {}), menus: [...menus, { key, name: `Menu ${index}`, items: [] }] } } }));
          }}>+ Add menu</Button>
        </>
      ) : null}

      {siteStylesSection === 'footer' ? (
        <>
          {siteStylesSectionCard('Footer layout', 'Site-wide footer visibility, width and copy.', <>
            <TextField select size="small" label="Footer" value={templateForm?.siteChrome?.footer?.enabled === false ? 'off' : 'on'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), footer: { ...(prev.siteChrome?.footer || {}), enabled: event.target.value === 'on' } } }))}>
              <MenuItem value="on">Enabled</MenuItem><MenuItem value="off">Hidden</MenuItem>
            </TextField>
            <TextField select size="small" label="Footer width" value={templateForm?.siteChrome?.footer?.content_width === 'full' ? 'full' : 'contained'} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), footer: { ...(prev.siteChrome?.footer || {}), content_width: event.target.value } } }))}>
              <MenuItem value="contained">Contained</MenuItem><MenuItem value="full">Full width</MenuItem>
            </TextField>
            <TextField size="small" label="Footer text" value={templateForm?.siteChrome?.footer?.text || ''} onChange={(event) => setTemplateForm((prev) => ({ ...prev, siteChrome: { ...(prev.siteChrome || {}), footer: { ...(prev.siteChrome?.footer || {}), text: event.target.value } } }))} helperText="Leave blank for the automatic copyright line." />
            <TextField
              select
              size="small"
              label="Elite Core watermark"
              value={templateForm?.siteChrome?.footer?.show_elite_core_watermark === false ? 'off' : 'on'}
              onChange={(event) => setTemplateForm((prev) => ({
                ...prev,
                siteChrome: {
                  ...(prev.siteChrome || {}),
                  footer: {
                    ...(prev.siteChrome?.footer || {}),
                    show_elite_core_watermark: event.target.value === 'on',
                  },
                },
              }))}
              helperText='Displays "Powered by Elite Core" with a link to elitecore.ca.'
            >
              <MenuItem value="on">Show</MenuItem>
              <MenuItem value="off">Hide</MenuItem>
            </TextField>
          </>)}
        </>
      ) : null}

      {siteStylesSection === 'advanced' ? (
        <>
          {siteStylesSectionCard('Design status', 'Draft/publish metadata for this design.', <>
            <TextField select size="small" label="Design status" value={templateStatus} onChange={(event) => setTemplateStatus(event.target.value)}>
              <MenuItem value="draft">Draft</MenuItem><MenuItem value="active">Active</MenuItem>
            </TextField>
          </>)}
          {siteStylesSectionCard('Theme & custom CSS', 'Low-level styling overrides for advanced use.', <>
            <Tabs value={templateCssEditorTab} onChange={(_event, value) => setTemplateCssEditorTab(value)} variant="fullWidth">
              <Tab label="Theme styling" value="theme" />
              <Tab label="Advanced CSS" value="custom" />
            </Tabs>
            {templateCssEditorTab === 'theme' ? (
              <TextField multiline minRows={11} size="small" label="Theme styling" value={templateForm.templateCss} onChange={(event) => setTemplateForm((prev) => ({ ...prev, templateCss: event.target.value }))} />
            ) : (
              <TextField multiline minRows={11} size="small" label="Advanced CSS" value={templateForm.customCss} onChange={(event) => setTemplateForm((prev) => ({ ...prev, customCss: event.target.value }))} />
            )}
            <Button size="small" variant="text" onClick={() => setShowSelectorContract((prev) => !prev)}>
              {showSelectorContract ? 'Hide developer details' : 'Show developer details'}
            </Button>
            {showSelectorContract ? (
              <Typography variant="caption" className="ps-builder-code-note">
                Stable selectors include .promoshowcase-slot .ps-widget--hero, .ps-widget--category_grid, .ps-widget--product_grid, .ps-widget--product_carousel, .ps-widget--button_row, .ps-widget--rich_text and .ps-widget--contact_block.
              </Typography>
            ) : null}
          </>)}
        </>
      ) : null}
    </Box>
  );

  const templatesView = (
    <Box className="ps-builder-panel-stack">
      <TemplateGallery
        templates={templateGalleryTemplates}
        selectedTemplateId={selectedTemplateId}
        activeTemplateId={settings?.activeTemplate?.template_id}
        onSelectTemplate={(tpl) => {
          setSelectedBuilderWidgetIndex(null);
          selectTemplateAndHydrate(tpl.id);
        }}
        onApplyTemplate={(tpl) => {
          if (String(tpl.id) !== String(selectedTemplateId)) selectTemplateAndHydrate(tpl.id);
          else applySelectedTemplate();
        }}
        compact
      />
      <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={resetBuilderFromSelectedTemplate} disabled={!selectedTemplate}>Reset</Button>
        <Button size="small" variant="outlined" onClick={loadTemplates}>Refresh</Button>
      </Box>
    </Box>
  );

  const settingsView = (
    <Box className="ps-builder-panel-stack">
      <Typography sx={{ fontWeight: 800 }}>Page preview</Typography>
      <Button variant={showSelectorOverlay ? 'contained' : 'outlined'} onClick={() => setShowSelectorOverlay((prev) => !prev)}>
        {showSelectorOverlay ? 'Block highlight on' : 'Block highlight off'}
      </Button>
      <Button variant="outlined" onClick={() => insertSelectorIntoCustomCss(selectedSelector)} disabled={!selectedSelector}>
        Use selected block in Advanced CSS
      </Button>
      <Divider />
      <Typography sx={{ fontWeight: 800 }}>Publishing</Typography>
      <Typography variant="caption" className="ps-builder-panel-copy">Status: {syncStatus === 'unknown' ? 'Not published yet' : syncStatus}</Typography>
      <Typography variant="caption" className="ps-builder-panel-copy">{layoutDirty ? 'You have changes that are not published yet.' : 'Your builder is up to date.'}</Typography>
      <Typography variant="caption" className="ps-builder-panel-copy">Published design: {settings?.activeTemplate?.template_id || 'n/a'}</Typography>
      <Typography variant="caption" className="ps-builder-panel-copy">Design history: {Array.isArray(settings?.templateHistory) ? settings.templateHistory.length : 0}</Typography>

      <Button variant="outlined" onClick={updateSelectedPrivateTemplate} disabled={templatesSaving || !selectedTemplate || selectedTemplate.visibility !== 'private'}>
        {templatesSaving ? 'Saving...' : 'Save Design Changes'}
      </Button>
    </Box>
  );

  const sidebarContent = {
    [BUILDER_SIDEBAR_VIEWS.ELEMENTS]: elementsView,
    [BUILDER_SIDEBAR_VIEWS.PAGES]: pagesView,
    [BUILDER_SIDEBAR_VIEWS.LAYERS]: layersView,
    [BUILDER_SIDEBAR_VIEWS.INSPECTOR]: inspectorView,
    [BUILDER_SIDEBAR_VIEWS.GLOBAL_STYLES]: globalStylesView,
    [BUILDER_SIDEBAR_VIEWS.TEMPLATES]: templatesView,
    [BUILDER_SIDEBAR_VIEWS.SETTINGS]: settingsView,
  }[sidebarView];

  const sidebarSubtitle = sidebarView === BUILDER_SIDEBAR_VIEWS.INSPECTOR && selectedBuilderWidget
    ? getWidgetLabel(selectedBuilderWidget.component)
    : sidebarView === BUILDER_SIDEBAR_VIEWS.PAGES
      ? `Editing ${selectedBuilderSlotMeta?.label || selectedBuilderSlot}`
      : undefined;

  return (
    <Box className="ps-builder-live-integration">
      {templateFormErrors.length > 0 ? (
        <Box
          ref={publishValidationRef}
          className="ps-builder-validation-banner"
          role="alert"
          tabIndex={-1}
          aria-label="Publish validation issues"
        >
          {templateFormErrors.map((error, index) => (
            <Typography key={`${error}-${index}`} variant="caption">{error}</Typography>
          ))}
        </Box>
      ) : null}

      <PromoShowcaseBuilderWorkspace
        deviceMode={deviceMode}
        toolbarProps={{
          templateName: selectedTemplate?.name || settings?.activeTemplate?.template_id || 'No template selected',
          pageName: selectedBuilderSlotMeta?.label || selectedBuilderSlot,
          syncStatus,
          onDeviceModeChange: setDeviceMode,
          onPreview: () => setBuilderCanvasMode('preview'),
          onCloseFocusMode,
          focusMode,
          onSavePrivateTemplate: savePrivateTemplate,
          onApplyTemplate: applySelectedTemplate,
          onOpenMoreActions: (event) => setMoreAnchor(event.currentTarget),
          applying: templatesApplying,
          saving: templatesSaving,
          canApply: !!selectedTemplateId,
          hasUnsavedChanges: layoutDirty,
        }}
        sidebarProps={{
          activeView: sidebarView,
          onViewChange: setSidebarView,
          subtitle: sidebarSubtitle,
          children: sidebarContent,
        }}
      >
        <Box className="ps-builder-canvas-header">
          <Box>
            <Typography sx={{ fontWeight: 800 }}>{selectedBuilderSlotMeta?.label || selectedBuilderSlot} Page</Typography>
            <Typography variant="caption" className="ps-builder-panel-copy">
              {currentBuilderWidgets.length} block{currentBuilderWidgets.length === 1 ? '' : 's'}{layoutDirty ? ' · Unsaved changes' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
            {canUndoLayoutImport ? (
              <Button size="small" variant="outlined" onClick={onUndoLayoutImport}>Undo layout</Button>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .35, mr: .4 }}>
              <Button
                size="small"
                variant={previewRendererMode === 'local' ? 'contained' : 'text'}
                onClick={() => setPreviewRendererMode?.('local')}
                sx={{ minWidth: 0, px: .9, fontSize: 11 }}
              >
                Instant
              </Button>
              <Tooltip title={settings?.templateCapabilities?.draft_preview === true ? 'Render this unsaved draft with the connected WordPress runtime' : 'Connected WordPress runtime does not advertise draft preview yet'}>
                <span>
                  <Button
                    size="small"
                    variant={previewRendererMode === 'wordpress' ? 'contained' : 'text'}
                    onClick={() => setPreviewRendererMode?.('wordpress')}
                    disabled={settings?.templateCapabilities?.draft_preview !== true}
                    sx={{ minWidth: 0, px: .9, fontSize: 11 }}
                  >
                    WordPress
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {builderCanvasMode === 'preview' && currentBuilderWidgets.length > 1 ? (
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Use ⋮⋮ to move · Click or drag-select text to edit
              </Typography>
            ) : null}
            <Tooltip title="Preview page">
              <IconButton size="small" className={builderCanvasMode === 'preview' ? 'is-active' : ''} onClick={() => setBuilderCanvasMode('preview')}>
                <VisibilityRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Page structure">
              <IconButton size="small" className={builderCanvasMode === 'blocks' ? 'is-active' : ''} onClick={() => setBuilderCanvasMode('blocks')}>
                <ViewQuiltRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {siteUrl ? (
              <Tooltip title="Open published site">
                <IconButton size="small" onClick={() => window.open(siteUrl, '_blank')}>
                  <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        {builderCanvasMode === 'preview' ? (
          <Box className="ps-builder-rendered-preview">
            <iframe
              ref={previewFrameRef}
              title={previewRendererMode === 'wordpress' ? 'WordPress-rendered template preview' : 'Template styles live preview'}
              sandbox="allow-same-origin allow-scripts"
              srcDoc={renderedPreviewSrcDoc}
              onLoad={() => {
                installPreviewDragAndDrop();
                installPreviewInlineEditing();
              }}
            />
            {previewRendererMode === 'wordpress' && wordpressPreviewLoading ? (
              <Box className="ps-builder-selector-badge" sx={{ top: 12, right: 12, bottom: 'auto', left: 'auto' }}>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>Rendering in WordPress…</Typography>
              </Box>
            ) : null}
            {previewRendererMode === 'wordpress' && wordpressPreviewError ? (
              <Box sx={{ position: 'absolute', left: 12, right: 12, bottom: 12, p: 1.1, borderRadius: 1.5, bgcolor: '#fff7ed', border: '1px solid #fdba74', zIndex: 5 }}>
                <Typography variant="caption" sx={{ color: '#9a3412', fontWeight: 700 }}>
                  {wordpressPreviewError} Showing the instant preview until WordPress is available.
                </Typography>
              </Box>
            ) : null}
            {showSelectorOverlay && selectedSelector ? (
              <Box className="ps-builder-selector-badge">
                <Typography variant="caption">Selected selector</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>{selectedSelector}</Typography>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box className="ps-builder-block-map">
            {currentBuilderWidgets.length === 0 ? (
              <Box className="ps-builder-canvas-empty">
                <Typography sx={{ fontWeight: 800 }}>This page is empty</Typography>
                <Typography variant="body2" className="ps-builder-panel-copy">Choose an element from the left sidebar and add it.</Typography>
              </Box>
            ) : currentBuilderWidgets.map((widget, index) => {
              const meta = getWidgetMeta(widget.component);
              const BlockIcon = meta.icon;
              const active = selectedBuilderWidgetIndex === index;
              return (
                <Box
                  key={`${selectedBuilderSlot}-${index}`}
                  className={`ps-builder-block-card${active ? ' is-selected' : ''}`}
                  onClick={() => selectWidget(index)}
                >
                  <Box className="ps-builder-block-card__toolbar">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .7 }}>
                      <BlockIcon sx={{ color: meta.accent, fontSize: 17 }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{getWidgetLabel(widget.component)}</Typography>
                    </Box>
                    <Typography variant="caption" className="ps-builder-panel-copy">Block {index + 1}</Typography>
                  </Box>
                  {renderWidgetPreviewCard(widget, index, selectedBuilderSlot)}
                </Box>
              );
            })}
          </Box>
        )}
      </PromoShowcaseBuilderWorkspace>

      <PromoShowcaseStockMediaDialog
        open={stockMediaSlideIndex !== null}
        onClose={() => setStockMediaSlideIndex(null)}
        instanceUniqueId={instanceUniqueId}
        initialQuery={stockMediaQuery}
        onSelect={(media) => {
          if (stockMediaSlideIndex === null) return;
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'background_url', media.url);
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'background_alt', media.alt || '');
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'stock_provider', media.provider || 'pexels');
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'stock_credit', media.credit || '');
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'stock_credit_url', media.creditUrl || '');
          updateHeroSlide(selectedBuilderSlot, selectedBuilderWidgetIndex, stockMediaSlideIndex, 'stock_source_url', media.sourcePageUrl || '');
        }}
      />

      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        <MenuItem onClick={() => { setMoreAnchor(null); handleResyncNow(); }} disabled={templatesApplying}>Refresh Website</MenuItem>
        <MenuItem onClick={() => { setMoreAnchor(null); handleRollbackLatest(); }} disabled={templatesApplying}>Restore Previous Version</MenuItem>
        <MenuItem onClick={() => { setMoreAnchor(null); loadTemplates(); }}>Refresh Layouts</MenuItem>
      </Menu>
    </Box>
  );
}
